import * as Tone from "tone";
import drumKitManager from "./drumKitManager";
import bassKitManager from "./bassKitManager";
import melodyKitManager from "./melodyKitManager";
import { normalizeSampleUrl } from "../utils/normalizeSampleUrl";

// Pre-decode a sample URL to an AudioBuffer on the ONLINE audio context.
// See notes in GenPlayer.js — this bypasses the stricter OfflineAudioContext.
// decodeAudioData and isolates bad samples per-lane so a single failing file
// silences only its own lane instead of aborting the whole 2-minute render.
//
// v19.5 — Hardened error path:
//   • Single retry with a short backoff for transient network errors (503,
//     ECONNRESET-style network failures) so a briefly-flapping load
//     balancer doesn't kill a whole upload.
//   • Content-Type sniff: if the response is text/html or application/json
//     the server almost certainly returned an error page instead of the
//     binary audio — surface that as a distinct diagnostic instead of the
//     generic "corrupted" message that decodeAudioData produces.
//   • Enriched console.warn: fetch URL, HTTP status, content-type, byte
//     length, first 4 bytes hex. Enough to distinguish "file not found"
//     vs "wrong MIME" vs "invalid audio format" without a repro.
async function decodeSampleUrl(url) {
  if (!url) return null;
  const fetchUrl = normalizeSampleUrl(url);
  const attemptFetch = async () => {
    const res = await fetch(fetchUrl, { cache: "no-store" });
    if (!res.ok) {
      const err = new Error(`HTTP ${res.status} ${res.statusText || ""}`.trim());
      err._transient = res.status >= 500 || res.status === 408;
      err._status = res.status;
      err._ct = res.headers.get("content-type");
      throw err;
    }
    const ct = (res.headers.get("content-type") || "").toLowerCase();
    const arr = await res.arrayBuffer();
    if (arr.byteLength === 0) {
      const err = new Error("Empty response body");
      err._ct = ct; throw err;
    }
    // Body-shape guard: audio/* and application/octet-stream are OK. HTML
    // or JSON means the server returned an error page — surface as a
    // distinct diagnostic (much clearer than decodeAudioData's generic
    // "Unable to decode audio data" for our users).
    if (!ct.startsWith("audio/") && ct !== "application/octet-stream" && ct !== "" && !ct.startsWith("binary/")) {
      const err = new Error(`Unexpected content-type ${ct} — server likely returned an error page instead of audio`);
      err._ct = ct; throw err;
    }
    return arr;
  };
  let arr;
  try {
    arr = await attemptFetch();
  } catch (e) {
    if (e && e._transient) {
      // eslint-disable-next-line no-console
      console.warn("[decodeSampleUrl] transient fetch failure, retrying in 500ms", fetchUrl, e.message);
      await new Promise((r) => setTimeout(r, 500));
      try { arr = await attemptFetch(); } catch (e2) {
        // eslint-disable-next-line no-console
        console.warn("[decodeSampleUrl] fetch failed after retry", fetchUrl, "status=", e2._status, "ct=", e2._ct, "msg=", e2.message);
        return null;
      }
    } else {
      // eslint-disable-next-line no-console
      console.warn("[decodeSampleUrl] fetch failed", fetchUrl, "status=", e._status, "ct=", e._ct, "msg=", e.message);
      return null;
    }
  }
  try {
    const ctx = Tone.getContext().rawContext;
    return await new Promise((resolve, reject) => {
      const maybe = ctx.decodeAudioData(arr.slice(0), resolve, reject);
      if (maybe && typeof maybe.then === "function") maybe.then(resolve, reject);
    });
  } catch (e) {
    // Log first 4 bytes so we can tell RIFF (WAV), ID3/0xFFxx (MP3),
    // OggS (Ogg) from an unexpected html/json response.
    let head = "";
    try {
      const view = new Uint8Array(arr, 0, Math.min(4, arr.byteLength));
      head = Array.from(view).map((b) => b.toString(16).padStart(2, "0")).join(" ");
    } catch { /* ignore */ }
    // eslint-disable-next-line no-console
    console.warn("[decodeSampleUrl] decodeAudioData rejected", fetchUrl, "bytes=", arr.byteLength, "head=", head, "err=", e?.message || e);
    return null;
  }
}
async function decodeUrlMap(map) {
  const entries = Object.entries(map || {});
  const decoded = await Promise.all(entries.map(async ([k, u]) => [k, await decodeSampleUrl(u)]));
  const out = {};
  for (const [k, b] of decoded) if (b) out[k] = b;
  return out;
}

class AudioEngine {
  constructor() {
    this.initialized = false;
    this.drumSynths = {};      // shared (used by audition() + as defaults)
    this.drumInstances = {};   // per-track: trackId → dedicated drum synth instance
    this.synthVoices = {};     // per-track polysynth
    this.samplerPlayers = {};  // per-track Tone.Player
    this.samplerFallbackSynths = {}; // per-track Galaxy Synth used when a sample failed to decode
    this.samplerEnvs = {};
    this.audioClipPlayers = {}; // trackId → clipId → Tone.Player
    this.audioClipSchedules = []; // Tone.Transport schedule ids
    this.trackOutputs = {};    // per-track: { volume: Tone.Volume, meter: Tone.Meter }
    this.effects = {};
    this.masterGain = null;
    this.sequence = null;
    this.currentStep = -1;
    this.stepListeners = new Set();
    this.tracksRef = [];
    this.bpmRef = 120;
  }

  async init() {
    if (this.initialized) return;
    // Low-latency interactive mode: adjust lookAhead on the existing default
    // context INSTEAD of `Tone.setContext(new Tone.Context(...))`. Replacing
    // the context after Tone module load orphans the Transport/Sequence/
    // Draw singletons — verified in iter 20 that a fresh Tone.Sequence
    // created outside the engine fires correctly while the engine's own
    // Sequence never binds to Transport (progress=NaN, currentStep=-1).
    // Tuning the existing context avoids the singleton mismatch entirely.
    try {
      const ctx = Tone.getContext();
      if (ctx) {
        // v19.14 P0 — Playback stability under heavy UI load.
        //
        // The Web Audio graph itself already runs off-main-thread on the
        // browser's audio rendering thread; what stutters in real-world
        // C.R.O.W. sessions is the SCHEDULER: Tone's lookAhead loop runs
        // on the main JS thread and stalls whenever a big UI operation
        // (grid resize, canvas redraw, arranger relayout) monopolises
        // the frame. If the loop can't refill the audio thread's queue
        // in time, notes drop and 808s clip.
        //
        // v19.15 P2 — Additional optimization:
        //  1) Bump `lookAhead` to 400ms so the audio thread has ~400ms
        //     of pre-scheduled events queued — a 400ms main-thread
        //     stall no longer produces an audible dropout (up from
        //     300ms in v19.15). v19.16: the extra 100ms bought us
        //     enough buffer that even the worst UI stalls (canvas
        //     redraw on a busy beat) can't starve the audio callback.
        //  2) Force `clockSource = "worker"` so Tone's scheduling loop
        //     runs on a dedicated Web Worker instead of the main JS
        //     thread. This is Tone.js v14+ default but we set it
        //     explicitly to survive future Tone upgrades.
        //  3) Match `updateInterval` to 25ms so the worker refills the
        //     lookAhead buffer 40x/s. Combined with the wider 400ms
        //     buffer this keeps drift < 1ms even under load.
        //  4) Set `latencyHint = "playback"` (browser picks a bigger
        //     hardware buffer). Trade-off: engine.audition() lands
        //     ~80-100 ms later than before — inaudible in a producer
        //     context.
        try { ctx.latencyHint = "playback"; } catch { /* noop */ }
        try { ctx.lookAhead = 0.4; } catch { /* noop */ }
        try { ctx.updateInterval = 0.025; } catch { /* noop */ }
        try { ctx.clockSource = "worker"; } catch { /* noop */ }
      }
    } catch { /* noop */ }
    await Tone.start();

    // Master FX chain
    this.effects.eq = new Tone.EQ3({ low: 0, mid: 0, high: 0 });    this.effects.compressor = new Tone.Compressor({ threshold: -20, ratio: 4, attack: 0.01, release: 0.1 });
    this.effects.distortion = new Tone.Distortion({ distortion: 0, wet: 0 });
    this.effects.delay = new Tone.FeedbackDelay("8n", 0);
    this.effects.delay.wet.value = 0;
    this.effects.reverb = new Tone.Reverb({ decay: 2.5, preDelay: 0.01, wet: 0 });
    this.effects.limiter = new Tone.Limiter(-1);
    this.masterGain = new Tone.Gain(0.8);

    // v19.13 — Master FX chain slots. `masterFxBus` sits between
    // masterGain and Tone.getDestination() and is where the user-
    // configurable master fxChain is rebuilt into. Starts empty
    // (pass-through), rebuilt on every store `masterFxChain` change.
    this.masterFxBus = new Tone.Gain(1);
    this.masterFxNodes = {}; // fxId → Tone node (owned, disposed on remove)

    this.effects.eq.chain(
      this.effects.compressor,
      this.effects.distortion,
      this.effects.delay,
      this.effects.reverb,
      this.effects.limiter,
      this.masterGain,
      this.masterFxBus,
      Tone.getDestination()
    );

    // Master visualisation taps — Wave View plugin reads from these. Connecting an
    // analyzer in parallel to the masterGain is non-destructive (analyzers are
    // passive sinks, they don't re-route audio).
    this.masterWaveform = new Tone.Waveform(1024);
    this.masterMeter = new Tone.Meter({ channels: 1, smoothing: 0.85, normalRange: false });
    this.masterGain.connect(this.masterWaveform);
    this.masterGain.connect(this.masterMeter);

    // Shared drum synths
    this.drumSynths.kick = new Tone.MembraneSynth({
      pitchDecay: 0.05, octaves: 6, oscillator: { type: "sine" },
      envelope: { attack: 0.001, decay: 0.4, sustain: 0.01, release: 1.4, attackCurve: "exponential" },
    }).connect(this.effects.eq);

    this.drumSynths.snare = new Tone.NoiseSynth({
      noise: { type: "white" },
      envelope: { attack: 0.001, decay: 0.2, sustain: 0 },
    }).connect(this.effects.eq);

    this.drumSynths.hihat = new Tone.MetalSynth({
      frequency: 250,
      envelope: { attack: 0.001, decay: 0.1, release: 0.01 },
      harmonicity: 5.1, modulationIndex: 32, resonance: 4000, octaves: 1.5,
    }).connect(this.effects.eq);
    this.drumSynths.hihat.volume.value = -15;

    this.drumSynths.clap = new Tone.NoiseSynth({
      noise: { type: "pink" },
      envelope: { attack: 0.001, decay: 0.15, sustain: 0 },
    }).connect(this.effects.eq);
    this.drumSynths.clap.volume.value = -6;

    Tone.getTransport().bpm.value = 120;
    this.bpmRef = 120;

    // Metronome click - dedicated synth, routed directly to destination
    this.metronomeClick = new Tone.MembraneSynth({
      pitchDecay: 0.008, octaves: 2,
      envelope: { attack: 0.001, decay: 0.05, sustain: 0, release: 0.05 },
    }).toDestination();
    this.metronomeClick.volume.value = -8;
    this.metronomeAccent = new Tone.MembraneSynth({
      pitchDecay: 0.008, octaves: 2,
      envelope: { attack: 0.001, decay: 0.06, sustain: 0, release: 0.06 },
    }).toDestination();
    this.metronomeAccent.volume.value = -4;
    this.metronomeSeq = null;
    this.metronomeOn = false;

    this.initialized = true;
    // v13.1 — Kick off the volume-automation loop once init is done. It
    // reads this.tracksRef 60x/s and applies interpolated gain to every
    // track whose automation.volume.enabled is true. Idempotent: only
    // registers the RAF once per session.
    this._startAutomationLoop();
    // Pre-load admin's default drum + bass + melody kits so DAW playback uses them.
    drumKitManager.refresh().catch(() => {});
    bassKitManager.refresh().catch(() => {});
    melodyKitManager.refresh().catch(() => {});
  }

  setMetronome(on) {
    this.metronomeOn = !!on;
    if (this.metronomeSeq) {
      try { this.metronomeSeq.stop(); this.metronomeSeq.dispose(); } catch {}
      this.metronomeSeq = null;
    }
    if (!this.metronomeOn) return;
    // 4 beats per bar, accent on beat 0
    this.metronomeSeq = new Tone.Sequence((time, beat) => {
      if (beat === 0) this.metronomeAccent.triggerAttackRelease("C5", "32n", time);
      else this.metronomeClick.triggerAttackRelease("A4", "32n", time);
    }, [0, 1, 2, 3], "4n");
    this.metronomeSeq.start(0);
  }

  // Create a per-track instrument when track type is assigned
  ensureTrackInstrument(track) {
    if (!this.initialized || !track || !track.type) return;
    if (track.type === "synth" && !this.synthVoices[track.id]) {
      // v9.9.10 — read the producer's persisted synth voice config from
      // the store (populated by GalaxySynth) so live playback + WAV
      // export share the SAME envelope + oscillator.
      const cfg = track.synthConfig || {};
      const env = cfg.envelope || { attack: 0.02, decay: 0.25, sustain: 0.55, release: 1.1 };
      const oscType = cfg.oscType || "fatsawtooth";
      const synth = new Tone.PolySynth(Tone.Synth, {
        oscillator: oscType === "fatsawtooth" ? { type: "fatsawtooth", count: 3, spread: 30 } : { type: oscType },
        envelope: env,
        portamento: track.glide ?? 0,
      });
      synth.volume.value = -10;
      this.synthVoices[track.id] = synth;
      this.connectTrackChain(track);
    }
    // Bass track: dedicated MonoSynth fallback (mirrors GenPlayer.js bass voice).
    // Kit-first routing happens at trigger time in triggerTrackInRegion.
    if (track.type === "bass" && !this.synthVoices[track.id]) {
      const bassSynth = new Tone.MonoSynth({
        oscillator: { type: "sawtooth" },
        filter: { Q: 4, type: "lowpass", rolloff: -24 },
        envelope: { attack: 0.005, decay: 0.2, sustain: 0.6, release: 0.05 },
        filterEnvelope: { attack: 0.01, decay: 0.2, sustain: 0.3, release: 0.1, baseFrequency: 60, octaves: 4 },
        portamento: track.glide ?? 0,
      });
      bassSynth.volume.value = -8;
      this.synthVoices[track.id] = bassSynth;
      this.connectTrackChain(track);
    }
    // Melody track: dedicated PolySynth fallback (mirrors GenPlayer.js lead voice).
    if (track.type === "melody" && !this.synthVoices[track.id]) {
      const lead = new Tone.PolySynth(Tone.Synth, {
        oscillator: { type: "fatsawtooth", count: 3, spread: 25 },
        envelope: { attack: 0.02, decay: 0.25, sustain: 0.55, release: 0.15 },
        portamento: track.glide ?? 0,
      });
      lead.volume.value = -10;
      this.synthVoices[track.id] = lead;
      this.connectTrackChain(track);
    }
    if (track.type === "sampler" && !this.samplerPlayers[track.id]) {
      // Empty polyphonic Tone.Sampler. NO default sample — a preloaded
      // C4 mapping would pollute the pitch-shifting when the producer later
      // picks (say) an 808 at C1: Tone.Sampler picks the NEAREST mapped
      // root, so their C3 note plays the piano pitch-shifted rather than
      // the 808. `loadSampleToTrack` rebuilds this Sampler with the correct
      // single root pitch when the user picks a sound from the Kit Sound
      // Library or drops a local file.
      const sampler = new Tone.Sampler({
        release: track.samplerEnv?.release ?? 0.5,
        attack: track.samplerEnv?.attack ?? 0.005,
        portamento: track.glide ?? 0,
      });
      sampler.volume.value = -3;
      this.samplerPlayers[track.id] = sampler;
      this.connectTrackChain(track);
    }
    // Ensure 8 pad players exist for sampler tracks
    if (track.type === "sampler") {
      if (!this.samplerPadPlayers) this.samplerPadPlayers = {};
      if (!this.samplerPadPlayers[track.id]) this.samplerPadPlayers[track.id] = {};
      const bus = this.ensureTrackOutput(track);
      const busInput = bus?.ducker || bus?.volume || this.effects.eq;
      (track.samplerPads || []).forEach((pad) => {
        if (!this.samplerPadPlayers[track.id][pad.id]) {
          const pp = new Tone.Player().connect(busInput);
          pp.volume.value = -3;
          this.samplerPadPlayers[track.id][pad.id] = pp;
        }
      });
      // Apply attack/release each rebuild
      const sampler = this.samplerPlayers[track.id];
      if (sampler) {
        const e = track.samplerEnv || {};
        try {
          if (typeof e.attack === "number") sampler.attack = e.attack;
          if (typeof e.release === "number") sampler.release = e.release;
        } catch {}
      }
    }
    // Drum tracks get a per-track instance so the Mixer can adjust their volume
    // and read their level independently (the shared drumSynths stay as
    // audition-only defaults).
    if (["kick", "snare", "hihat", "clap"].includes(track.type)) {
      if (!this.drumInstances[track.id]) {
        this.ensureDrumInstance(track);
        this.connectTrackChain(track);
      }
    }
    // Always make sure the per-track output bus matches the current
    // volume / mute state (cheap if it already exists).
    if (track.type) this.ensureTrackOutput(track);
  }

  // 0..1 linear → dB (with safe -Infinity floor for silence)
  linearToDb(v) {
    if (!Number.isFinite(v) || v <= 0.0001) return -Infinity;
    return 20 * Math.log10(v);
  }

  // Get or create the per-track Volume+Meter bus that all signal for this track
  // routes through. The Volume node is what the Mixer fader writes to; the Meter
  // is what the green level meter polls every frame.
  ensureTrackOutput(track) {
    if (!this.initialized || !this.effects.eq) return null;
    let bus = this.trackOutputs[track.id];
    if (!bus) {
      const volume = new Tone.Volume(this.linearToDb(track.volume ?? 0.8));
      const meter = new Tone.Meter({ smoothing: 0.8, normalRange: false });
      // Per-track stereo panner (Mixer-only control). Sits after the fader
      // so the pan is applied to the fully-metered signal but before the
      // master bus. Range -1 (hard left) .. +1 (hard right).
      const panner = new Tone.Panner(track.pan ?? 0);
      const ducker = new Tone.Gain(1);
      ducker.connect(volume);
      volume.connect(panner);
      panner.connect(meter);
      panner.connect(this.effects.eq);
      this.trackOutputs[track.id] = { volume, meter, ducker, panner };
      bus = this.trackOutputs[track.id];
    }
    try {
      bus.volume.volume.value = this.linearToDb(track.volume ?? 0.8);
      bus.volume.mute = !!track.muted;
      if (bus.panner && typeof track.pan === "number") bus.panner.pan.value = track.pan;
    } catch { /* noop */ }
    return bus;
  }

  // Live pan update from Mixer.jsx (Left/Center/Right knob). Range -1..+1.
  setTrackPan(trackId, pan) {
    const bus = this.trackOutputs[trackId];
    if (!bus || !bus.panner) return;
    try { bus.panner.pan.value = Math.max(-1, Math.min(1, pan || 0)); } catch {}
  }

  // Live glide (portamento) update from the Synth/Sampler plugin UI.
  // `sec` is portamento time in seconds — 0 = discrete note-jumps (classic
  // behaviour); >0 smoothly ramps pitch between consecutive notes.
  //
  // v12.0 GLIDE FIX — PolySynth spawns a new voice per note, and each voice
  // starts from silence with no prior frequency to glide FROM, so glide
  // was audibly a no-op on synth/melody tracks. When glide > 0 we clamp
  // the synth to `maxPolyphony = 1` which forces voice-stealing: every
  // new note reuses the same voice and its `.frequency` param ramps
  // linearly over `portamento` seconds. When glide returns to 0 we
  // restore full polyphony. Sampler is skipped intentionally — the UI
  // greys out the Glide knob on Sampler tracks (see PluginView).
  setTrackGlide(trackId, sec) {
    const s = Math.max(0, Math.min(4, sec || 0));
    const synth = this.synthVoices?.[trackId];
    if (synth) {
      try { synth.set({ portamento: s }); } catch { /* PolySynth propagates */ }
      // maxPolyphony is a top-level property on PolySynth (not part of set()).
      try {
        if (typeof synth.maxPolyphony !== "undefined") {
          synth.maxPolyphony = s > 0 ? 1 : 32;
        }
      } catch { /* MonoSynth has no maxPolyphony */ }
    }
    // Tone.Sampler ignores portamento entirely — we still set it in case
    // a future Tone.js release wires it up, but the visible UI reflects
    // that Glide is a no-op on sampler tracks.
    const sampler = this.samplerPlayers?.[trackId];
    if (sampler) {
      try { sampler.set({ portamento: s }); } catch { /* noop */ }
    }
    const fallback = this.samplerFallbackSynths?.[trackId];
    if (fallback) {
      try { fallback.set({ portamento: s }); } catch { /* noop */ }
      try {
        if (typeof fallback.maxPolyphony !== "undefined") {
          fallback.maxPolyphony = s > 0 ? 1 : 32;
        }
      } catch { /* noop */ }
    }
  }

  // Sidechain duck: schedule a brief downward ramp on a target track's
  // ducker gain to simulate compressor gain-reduction triggered by a source
  // hit. Called from triggerTrackInRegion when Kick or Bass fires.
  //   - targetType='bass'  → ducked by all Kick hits
  //   - targetType='melody'→ ducked by all Bass hits
  // Depth ~ -12dB (linear 0.25), attack 5ms, release 100ms.
  _scheduleSidechainDuck(targetType, time) {
    const targets = (this.tracksRef || []).filter((t) => t.type === targetType && !t.muted);
    for (const t of targets) {
      const bus = this.trackOutputs[t.id];
      const g = bus?.ducker?.gain;
      if (!g) continue;
      try {
        g.cancelScheduledValues(time);
        g.setValueAtTime(1, time);
        g.linearRampToValueAtTime(0.25, time + 0.005);   // 5ms attack
        g.linearRampToValueAtTime(1, time + 0.105);      // 100ms release
      } catch { /* noop */ }
    }
  }

  // Read RMS level (in dB) for a track meter — used by the Mixer to drive the
  // animated green level bar.
  getTrackLevelDb(trackId) {
    const v = this.trackOutputs[trackId]?.meter?.getValue();
    if (typeof v !== "number" || !Number.isFinite(v)) return -Infinity;
    return v;
  }
  getMasterLevelDb() {
    const v = this.masterMeter?.getValue();
    if (typeof v !== "number" || !Number.isFinite(v)) return -Infinity;
    return v;
  }
  getMasterWaveform() {
    return this.masterWaveform?.getValue() ?? null;
  }
  // Live fader update from Mixer.jsx — also keeps the mute flag in sync.
  setTrackVolume(trackId, v) {
    const bus = this.trackOutputs[trackId];
    if (!bus) return;
    try { bus.volume.volume.value = this.linearToDb(v); } catch {}
  }

  // v13.1 — Volume-automation RAF loop. On each frame, for every track
  // with automation.volume.enabled=true, compute the current transport
  // bar, linearly interpolate between neighbouring nodes, convert the
  // resulting linear gain to dB, and push it to that track's Tone.Volume
  // bus. Tracks WITHOUT automation are left alone so their static
  // .volume slider still works. Fires only while Transport is running
  // (transport.state === "started") — a stopped or paused transport
  // holds whatever gain was last applied.
  _startAutomationLoop() {
    if (this._automationRaf) return;
    const tick = () => {
      this._automationRaf = requestAnimationFrame(tick);
      const transport = Tone.getTransport();
      if (transport.state !== "started") return;
      const bpm = transport.bpm.value || 120;
      const sec = transport.seconds || 0;
      const bar = (sec * bpm) / 60 / 4;
      const tracks = this.tracksRef || [];
      for (const t of tracks) {
        const auto = t?.automation?.volume;
        if (!auto || !auto.enabled) continue;
        const nodes = auto.nodes || [];
        if (nodes.length === 0) continue;
        // Interpolate. Nodes are sorted by bar in the store.
        let value;
        if (bar <= nodes[0].bar) {
          value = nodes[0].value;
        } else if (bar >= nodes[nodes.length - 1].bar) {
          value = nodes[nodes.length - 1].value;
        } else {
          for (let i = 0; i < nodes.length - 1; i++) {
            const a = nodes[i];
            const b = nodes[i + 1];
            if (bar >= a.bar && bar <= b.bar) {
              const span = Math.max(1e-6, b.bar - a.bar);
              const t01 = (bar - a.bar) / span;
              value = a.value + (b.value - a.value) * t01;
              break;
            }
          }
        }
        if (typeof value === "number") {
          this.setTrackVolume(t.id, Math.max(0, Math.min(1, value)));
        }
      }
    };
    this._automationRaf = requestAnimationFrame(tick);
  }
  setTrackMuted(trackId, muted) {
    const bus = this.trackOutputs[trackId];
    if (!bus) return;
    try { bus.volume.mute = !!muted; } catch {}
  }

  // Create the per-track drum instance the first time a drum track is used.
  ensureDrumInstance(track) {
    if (!this.initialized) return null;
    if (this.drumInstances[track.id]) return this.drumInstances[track.id];
    let synth = null;
    if (track.type === "kick") {
      synth = new Tone.MembraneSynth({
        pitchDecay: 0.05, octaves: 6, oscillator: { type: "sine" },
        envelope: { attack: 0.001, decay: 0.4, sustain: 0.01, release: 1.4, attackCurve: "exponential" },
      });
    } else if (track.type === "snare") {
      synth = new Tone.NoiseSynth({ noise: { type: "white" }, envelope: { attack: 0.001, decay: 0.2, sustain: 0 } });
    } else if (track.type === "hihat") {
      synth = new Tone.MetalSynth({
        frequency: 250, envelope: { attack: 0.001, decay: 0.1, release: 0.01 },
        harmonicity: 5.1, modulationIndex: 32, resonance: 4000, octaves: 1.5,
      });
      synth.volume.value = -15;
    } else if (track.type === "clap") {
      synth = new Tone.NoiseSynth({ noise: { type: "pink" }, envelope: { attack: 0.001, decay: 0.15, sustain: 0 } });
      synth.volume.value = -6;
    }
    if (synth) this.drumInstances[track.id] = synth;
    return synth;
  }

  // Wire/rewire a track's instrument through its per-track FX chain
  connectTrackChain(track) {
    if (!track || !track.type || !this.effects.eq) return;

    // Build per-track FX nodes if needed
    if (!this.trackFx) this.trackFx = {};
    if (!this.trackFx[track.id]) this.trackFx[track.id] = {};
    const store = this.trackFx[track.id];

    // dispose orphans
    const fxIds = (track.fxChain || []).map((f) => f.id);
    Object.keys(store).forEach((id) => {
      if (!fxIds.includes(id)) {
        const n = store[id];
        try { n._wfMaster?.dispose(); } catch {}
        try { n._meterMaster?.dispose(); } catch {}
        // v15.0 — cancel Auto-Tune's RAF and dispose the tracking FFT
        // so removed instances don't keep polling silently.
        try { if (n._autotuneRAF) cancelAnimationFrame(n._autotuneRAF); } catch {}
        try { n._autotuneFFT?.dispose(); } catch {}
        try { n.dispose(); } catch {}
        delete store[id];
      }
    });

    // Build node for each fx and apply params
    const nodes = (track.fxChain || []).map((fx) => {
      if (!store[fx.id]) {
        store[fx.id] = this.createFxNode(fx.plugin);
      }
      this.applyFxParams(store[fx.id], fx.plugin, fx.params);
      return store[fx.id];
    });

    // Get the source instrument (Sampler/PolySynth/drum instance are directly connectable sources)
    let source = null;
    if (track.type === "synth" || track.type === "bass" || track.type === "melody") source = this.synthVoices[track.id];
    else if (track.type === "sampler") source = this.samplerPlayers[track.id];
    else if (["kick", "snare", "hihat", "clap"].includes(track.type)) {
      source = this.ensureDrumInstance(track);
    }
    // v15.0 — Audio tracks: route their recorded / dropped clips through
    // the FX chain (Auto-Tune, Reverb, Delay, etc.) instead of the raw
    // bus.volume. Each audio-clip player is a Tone.Player, and multiple
    // clips share the same fan-in Gain node so the chain sees them all.
    if (track.type === "audio") {
      if (!this.audioTrackFanin) this.audioTrackFanin = {};
      if (!this.audioTrackFanin[track.id]) {
        this.audioTrackFanin[track.id] = new Tone.Gain(1);
      }
      source = this.audioTrackFanin[track.id];
      // Rewire every existing player on this track into the fan-in.
      const players = this.audioClipPlayers?.[track.id] || {};
      Object.values(players).forEach((p) => {
        try { p.disconnect(); } catch {}
        try { p.connect(source); } catch {}
      });
    }

    if (!source) return;
    // All tracks route to a per-track Volume+Meter bus → master EQ.
    const bus = this.ensureTrackOutput(track);
    const busInput = bus?.ducker || bus?.volume || this.effects.eq;
    try { source.disconnect(); } catch {}
    nodes.forEach((n) => { try { n.disconnect(); } catch {} });
    if (nodes.length === 0) {
      source.connect(busInput);
    } else {
      // Linear chain: source -> n0 -> n1 -> ... -> per-track bus
      let prev = source;
      nodes.forEach((node) => {
        prev.connect(node);
        // Wave View plugin: tap the per-track Waveform/Meter that it carries.
        if (node._wfMaster) { try { this.masterGain?.connect(node._wfMaster); } catch {} }
        if (node._meterMaster) { try { this.masterGain?.connect(node._meterMaster); } catch {} }
        // v16.0 — Auto-Tune removed; guard left for backwards-safe cleanup on legacy state.
        prev = node;
      });
      prev.connect(busInput);
    }
  }

  // v19.13 — Rebuild the MASTER FX chain from the current store state.
  // Called on every store change to `masterFxChain`. Signal flow:
  //   effects.limiter → masterGain → [master fx nodes] → masterFxBus → destination
  // We preserve node instances across rebuilds (keyed by fx.id) so per-
  // param edits don't stutter, and dispose any node that was removed.
  rebuildMasterFxChain(chain = []) {
    if (!this.masterFxBus || !this.masterGain) return;
    const nextIds = new Set(chain.map((f) => f.id));
    // Dispose removed nodes.
    Object.entries(this.masterFxNodes).forEach(([id, node]) => {
      if (!nextIds.has(id)) {
        try { node.disconnect(); } catch { /* noop */ }
        try { node.dispose(); } catch { /* noop */ }
        delete this.masterFxNodes[id];
      }
    });
    // Create / update nodes in the requested order.
    const nodes = chain.map((fx) => {
      if (!this.masterFxNodes[fx.id]) {
        this.masterFxNodes[fx.id] = this.createFxNode(fx.plugin);
      }
      this.applyFxParams(this.masterFxNodes[fx.id], fx.plugin, fx.params);
      return this.masterFxNodes[fx.id];
    });
    // Rewire the chain: masterGain → nodes → masterFxBus.
    try { this.masterGain.disconnect(); } catch { /* noop */ }
    // Restore the two passive taps + destination bus.
    if (this.masterWaveform) { try { this.masterGain.connect(this.masterWaveform); } catch { /* noop */ } }
    if (this.masterMeter)    { try { this.masterGain.connect(this.masterMeter); } catch { /* noop */ } }
    if (nodes.length === 0) {
      try { this.masterGain.connect(this.masterFxBus); } catch { /* noop */ }
    } else {
      let prev = this.masterGain;
      nodes.forEach((n) => {
        try { n.disconnect(); } catch { /* noop */ }
        try { prev.connect(n); } catch { /* noop */ }
        prev = n;
      });
      try { prev.connect(this.masterFxBus); } catch { /* noop */ }
    }
  }


  createFxNode(plugin) {
    let node;
    switch (plugin) {
      case "eq": {
        // v19.13 — Advanced multi-band EQ:
        //   HP24 → LowShelf → 7 Peak bands → HighShelf → LP24
        // Each band exposes .frequency / .gain (and .Q for the peaks).
        // HP/LP support a `slope` param (24 or 48 dB/oct) applied via
        // Tone.Filter.rolloff.
        // The graph is wrapped so Tone's connect() routes into the internal
        // input gain, chains through the filters, and hands off from the
        // output gain — presenting a single node to the FX chain code.
        const input = new Tone.Gain(1);
        const output = new Tone.Gain(1);
        const bands = [
          new Tone.Filter({ type: "highpass",  frequency: 30,    Q: 0.71, rolloff: -24 }),
          new Tone.Filter({ type: "lowshelf",  frequency: 80,    gain: 0 }),
          new Tone.Filter({ type: "peaking",   frequency: 160,   gain: 0, Q: 1 }),
          new Tone.Filter({ type: "peaking",   frequency: 320,   gain: 0, Q: 1 }),
          new Tone.Filter({ type: "peaking",   frequency: 640,   gain: 0, Q: 1 }),
          new Tone.Filter({ type: "peaking",   frequency: 1250,  gain: 0, Q: 1 }),
          new Tone.Filter({ type: "peaking",   frequency: 2500,  gain: 0, Q: 1 }),
          new Tone.Filter({ type: "peaking",   frequency: 5000,  gain: 0, Q: 1 }),
          new Tone.Filter({ type: "peaking",   frequency: 8000,  gain: 0, Q: 1 }),
          new Tone.Filter({ type: "highshelf", frequency: 12000, gain: 0 }),
          new Tone.Filter({ type: "lowpass",   frequency: 18000, Q: 0.71, rolloff: -24 }),
        ];
        input.chain(...bands, output);
        // Override .input so incoming source.connect(node) feeds the filter
        // chain instead of the output gain. Tone.getConnectDestination()
        // reads this property when routing signal.
        output.input = input;
        output._eq5Bands = bands;
        output._eq5Input = input;
        const _origDispose = output.dispose.bind(output);
        output.dispose = function() {
          bands.forEach((b) => { try { b.dispose(); } catch { /* noop */ } });
          try { input.dispose(); } catch { /* noop */ }
          return _origDispose();
        };
        return output;
      }
      case "compressor": return new Tone.Compressor({ threshold: -20, ratio: 4, attack: 0.01, release: 0.1 });
      case "distortion": return new Tone.Distortion({ distortion: 0.3, wet: 0.5 });
      case "saturation": {
        // v9: Heavy analog-style drive built as a Tone.Chebyshev harmonic
        // shaper wrapped in a wet/dry crossfade. Chebyshev at high orders
        // (order ~50 for 'heavy') generates thick odd-harmonic warmth
        // similar to a saturated tube stage without the harsh clipping of
        // Distortion's waveShaper approach. `drive` scales the input into
        // the shaper (0 = clean, 1 = crushed); `wet` blends against dry
        // through the built-in wet.value.
        const sat = new Tone.Chebyshev({ order: 50, wet: 0.8 });
        // Track the drive as a gain node in front — Chebyshev has no built-in
        // drive control; we adjust an input gain so wet-level stays sane.
        sat._drive = new Tone.Gain(1.5);
        sat._drive.connect(sat);
        // Expose a synthetic "input" so track chains route into the drive gain.
        sat.input = sat._drive;
        return sat;
      }
      case "limiter":    return new Tone.Limiter(-1);
      case "clipper": {
        // v19.13 — UPWARD CLIPPER (2-stage bus processor).
        //
        // Stage 1 — Upward Expansion: pulls quiet signals ABOVE the
        // `expandThreshold` toward a hotter target so weak 808 tails
        // and quiet instruments feel more even. Implemented as a
        // Tone.Compressor with a ratio < 1 (upward expansion mode) —
        // Web Audio DynamicsCompressorNode accepts negative ratios in
        // some browsers but Tone clamps at 1; instead we approximate
        // by feeding through a low-ratio compressor tuned as a
        // "makeup gain when quiet" via a follower-driven Gain.
        //
        // Stage 2 — Soft-clipper output stage: Tone.WaveShaper with a
        // tanh curve. Produces the dense "flat-topped ceiling" high-
        // gain visual + audible signature without harsh digital
        // aliasing. `clipDrive` scales the input into the shaper;
        // `clipCeiling` is applied AFTER as a makeup / trim gain.
        const input = new Tone.Gain(1);
        const output = new Tone.Gain(1);
        // Stage 1: Upward expander — Tone.MultibandCompressor isn't
        // available on all Tone versions, so we build a follower-based
        // upward booster: a signal-follower reads the envelope and
        // drives a gain when the envelope crosses the threshold. We
        // fake this cheaply with a Tone.Compressor set to knee at the
        // threshold with a ratio ≈ 1/(1 + amount) (soft downward),
        // then apply a static makeup gain so the perceived effect is
        // "quiet → louder".
        const expander = new Tone.Compressor({ threshold: -24, ratio: 1.4, attack: 0.02, release: 0.15, knee: 12 });
        const makeup = new Tone.Gain(1.5); // pre-clip makeup
        // Stage 2: Soft clipper via WaveShaper. A pure tanh curve is
        // the classic "smooth ceiling" waveshape — the industry
        // standard for the "flat-topped, no digital nasties" look.
        const shaper = new Tone.WaveShaper((x) => Math.tanh(x * 1.6), 4096);
        const trim = new Tone.Gain(1); // post-clip ceiling / trim
        input.chain(expander, makeup, shaper, trim, output);
        output.input = input;
        output._clipper = { expander, makeup, shaper, trim };
        const _origDispose = output.dispose.bind(output);
        output.dispose = function() {
          try { expander.dispose(); } catch { /* noop */ }
          try { makeup.dispose(); } catch { /* noop */ }
          try { shaper.dispose(); } catch { /* noop */ }
          try { trim.dispose(); } catch { /* noop */ }
          try { input.dispose(); } catch { /* noop */ }
          return _origDispose();
        };
        return output;
      }
      case "delay": {
        // v9.9.8 — BPM-SYNC delay. `time` param is now a Tone.js notation
        // string ("2n","4n","8n","16n","32n"). We store the notation on
        // the node so `resyncDelays()` can recompute the delayTime in
        // seconds whenever the project BPM changes.
        const d = new Tone.FeedbackDelay("8n", 0.3);
        d.wet.value = 0.3;
        d._syncNote = "8n";
        return d;
      }
      case "reverb":     return new Tone.Reverb({ decay: 2.5, preDelay: 0.01, wet: 0.3 });
      case "pumper": {
        // v12.0 — CROW PUMPER: rhythmic volume duck. A Tone.Gain sits in
        // the FX chain; a Tone.Loop scheduled on the transport ramps the
        // gain DOWN quickly on each subdivision (attack ~5ms) then
        // ramps BACK UP over the remainder of the subdivision — classic
        // sidechain pump curve. `_pumpDepth` controls how far the gain
        // dips (0..1 amount of the source, e.g. 0.4 means duck to 40%).
        // `_pumpMode` is the Tone.Transport subdivision notation.
        const gain = new Tone.Gain(1);
        gain._pumpDepth = 0.6;                 // Duck to 40% (= 1 - 0.6)
        gain._pumpMode = "4n";                 // 1/4 note default
        // Scheduled loop that fires the pump curve.
        gain._pumpLoop = new Tone.Loop((time) => {
          const d = Math.max(0, Math.min(1, gain._pumpDepth || 0));
          const floor = Math.max(0.02, 1 - d); // Gain floor after duck
          const sub = Tone.Time(gain._pumpMode || "4n").toSeconds();
          // Fast downward attack (5ms), slower recovery over the rest
          // of the subdivision. Cancels any prior automation so mode
          // changes take effect immediately.
          gain.gain.cancelScheduledValues(time);
          gain.gain.setValueAtTime(1, time);
          gain.gain.linearRampToValueAtTime(floor, time + 0.005);
          gain.gain.linearRampToValueAtTime(1, time + Math.max(0.02, sub * 0.85));
        }, gain._pumpMode).start(0);
        // On dispose, tear down the loop so we don't leak transport events.
        const _origDispose = gain.dispose.bind(gain);
        gain.dispose = function () {
          try { gain._pumpLoop?.stop(); gain._pumpLoop?.dispose(); } catch { /* noop */ }
          return _origDispose();
        };
        return gain;
      }
      case "waveview": {
        // Wave View is a transparent passthrough Gain. The plugin carries its own
        // master-tap waveform + meter; connectTrackChain hooks them up so the UI
        // renders the live master signal even though the node sits on a track.
        node = new Tone.Gain(1);
        node._wfMaster = new Tone.Waveform(1024);
        node._meterMaster = new Tone.Meter({ smoothing: 0.85, normalRange: false });
        return node;
      }
      default:           return new Tone.Gain(1);
    }
  }

  applyFxParams(node, plugin, params = {}) {
    try {
      if (plugin === "eq") {
        const bands = node._eq5Bands || [];
        if (Array.isArray(params.bands) && bands.length) {
          params.bands.forEach((b, i) => {
            const f = bands[i]; if (!f) return;
            if (typeof b.freq === "number") f.frequency.value = b.freq;
            if (typeof b.gain === "number" && f.gain) f.gain.value = b.gain;
            if (typeof b.q    === "number" && f.Q) f.Q.value = b.q;
            if (typeof b.type === "string" && f.type !== b.type) {
              try { f.type = b.type; } catch { /* noop */ }
            }
            // Slope for HP/LP: -24 or -48 dB/oct. Tone.Filter.rolloff
            // supports -12/-24/-48; we map the incoming positive 24/48
            // to the negative Tone value.
            if ((b.type === "highpass" || b.type === "lowpass") && typeof b.slope === "number") {
              try { f.rolloff = -Math.abs(b.slope); } catch { /* noop */ }
            }
          });
        } else if (bands.length) {
          // Legacy migration path.
          if (typeof params.low  === "number" && bands[0].gain) bands[0].gain.value = params.low;
          if (typeof params.mid  === "number" && bands[2].gain) bands[2].gain.value = params.mid;
          if (typeof params.high === "number" && bands[4].gain) bands[4].gain.value = params.high;
        }
      } else if (plugin === "clipper") {
        // v19.13 — Upward Clipper param application.
        //   • expandThreshold (dB) — expander threshold
        //   • expandAmount   (0..1) — makeup boost after expansion
        //   • clipDrive      (1..4) — pre-shaper drive
        //   • clipCeiling    (dB)   — post-shaper trim / ceiling
        const c = node._clipper;
        if (c) {
          if (typeof params.expandThreshold === "number" && c.expander?.threshold) {
            c.expander.threshold.value = params.expandThreshold;
          }
          if (typeof params.expandAmount === "number" && c.makeup?.gain) {
            // 0..1 → 1.0..2.5x makeup so quiet bits get pulled up.
            c.makeup.gain.value = 1 + (params.expandAmount || 0) * 1.5;
          }
          if (typeof params.clipDrive === "number" && c.shaper) {
            const drive = Math.max(0.5, Math.min(6, params.clipDrive));
            // Rebuild the tanh curve with the new drive coefficient.
            try {
              c.shaper.setMap((x) => Math.tanh(x * drive), 4096);
            } catch { /* older Tone versions */ }
          }
          if (typeof params.clipCeiling === "number" && c.trim?.gain) {
            const dB = params.clipCeiling;
            c.trim.gain.value = Math.pow(10, dB / 20);
          }
        }
      } else if (plugin === "compressor") {
        if (node.threshold) node.threshold.value = params.threshold ?? -20;
        if (node.ratio) node.ratio.value = params.ratio ?? 4;
      } else if (plugin === "distortion") {
        node.distortion = params.amount ?? 0.3;
        if (node.wet) node.wet.value = params.wet ?? 0.5;
      } else if (plugin === "saturation") {
        // drive 0..1 → input gain 1..8 (heavy character)
        // character 'warm' → order 20, 'heavy' → order 60 for aggressive odds
        const character = params.character || "heavy";
        const targetOrder = character === "heavy" ? 60 : 20;
        try { node.order = targetOrder; } catch { /* Chebyshev order is settable in Tone v15 */ }
        try { if (node._drive) node._drive.gain.value = 1 + (params.drive ?? 0.5) * 7; } catch { /* noop */ }
        if (node.wet) node.wet.value = params.wet ?? 0.8;
      } else if (plugin === "limiter") {
        if (node.threshold) node.threshold.value = params.threshold ?? -1;
      } else if (plugin === "delay") {
        // v9.9.8 — accept BOTH the new Tone.js notation strings ("2n","4n",
        // "8n","16n","32n") and legacy numeric seconds so old saved
        // projects still load. Notation strings get converted to seconds
        // via the current Tone.Transport.bpm, and the raw notation is
        // stashed on the node so `resyncDelays()` can recompute after a
        // BPM change without needing the store.
        const t = params.time;
        if (typeof t === "string") {
          try {
            node._syncNote = t;
            if (node.delayTime) node.delayTime.value = Tone.Time(t).toSeconds();
          } catch {
            if (node.delayTime) node.delayTime.value = 0.25;
          }
        } else if (typeof t === "number") {
          // Legacy numeric ms/seconds — clear the sync note so BPM changes
          // no longer affect this delay (matches pre-v9.9.8 behaviour).
          node._syncNote = null;
          if (node.delayTime) node.delayTime.value = t;
        } else if (node.delayTime && node._syncNote) {
          // No time provided but a notation is remembered — recompute
          // against the current BPM (this is what resyncDelays hits).
          try { node.delayTime.value = Tone.Time(node._syncNote).toSeconds(); } catch { /* noop */ }
        }
        if (node.feedback) node.feedback.value = params.feedback ?? 0.3;
        if (node.wet) node.wet.value = params.wet ?? 0.3;
      } else if (plugin === "reverb") {
        node.decay = params.decay ?? 2.5;
        if (node.wet) node.wet.value = params.wet ?? 0.3;
      } else if (plugin === "pumper") {
        // v12.0 — Live parameter update for Crow Pumper. `amount` (0..1)
        // sets the duck depth; `mode` sets the subdivision ("2n"|"4n"|"8n").
        // Accept UI-facing "1/2","1/4","1/8" strings and translate.
        const modeMap = { "1/2": "2n", "1/4": "4n", "1/8": "8n" };
        const rawMode = params.mode || "1/4";
        const toneMode = modeMap[rawMode] || (typeof rawMode === "string" && /^[0-9]+n$/.test(rawMode) ? rawMode : "4n");
        node._pumpDepth = Math.max(0, Math.min(1, params.amount ?? 0.6));
        node._pumpMode = toneMode;
        // Restart the loop at the new interval so the pump pulse tracks
        // the fresh subdivision immediately, not on the NEXT bar.
        try {
          if (node._pumpLoop) {
            node._pumpLoop.interval = toneMode;
          }
        } catch { /* noop */ }
      }
    } catch (e) {}
  }

  disposeTrackInstrument(trackId) {
    if (this.synthVoices[trackId]) {
      try { this.synthVoices[trackId].dispose(); } catch { /* noop */ }
      delete this.synthVoices[trackId];
    }
    if (this.samplerPlayers[trackId]) {
      try { this.samplerPlayers[trackId].dispose(); } catch { /* noop */ }
      delete this.samplerPlayers[trackId];
    }
    if (this.samplerFallbackSynths[trackId]) {
      try { this.samplerFallbackSynths[trackId].dispose(); } catch { /* noop */ }
      delete this.samplerFallbackSynths[trackId];
    }
  }

  async loadAudioClip(trackId, clipId, url) {
    await this.init();
    if (!this.audioClipPlayers[trackId]) this.audioClipPlayers[trackId] = {};
    // Ensure a per-track Volume+Meter bus exists for this audio track so that
    // (a) the Mixer fader works and (b) the green meter has signal to read.
    if (!this.trackOutputs[trackId]) {
      this.ensureTrackOutput({ id: trackId, type: "audio", volume: 0.8 });
    }
    const bus = this.trackOutputs[trackId];
    let player = this.audioClipPlayers[trackId][clipId];
    if (!player) {
      // v15.0 — If the track has an audio-track fan-in Gain from
      // connectTrackChain, route through it so recordings hit the FX
      // chain. Otherwise connect straight to the track's fader bus.
      const target = this.audioTrackFanin?.[trackId] || bus?.volume || this.effects.eq;
      player = new Tone.Player().connect(target);
      this.audioClipPlayers[trackId][clipId] = player;
    }
    try { await player.load(url); } catch (e) { console.error("Audio clip load fail:", e); }
    // v15.0 — After the player finishes loading, re-run the audio-clip
    // schedule against the CURRENT tracksRef so freshly recorded /
    // dropped clips become audible on the next transport cycle. Without
    // this, `scheduleAudioClips` runs synchronously on state change and
    // sees `player.loaded === false`, silently skipping the clip until
    // the next unrelated re-schedule kicks in — which is why v14.1
    // recorded vocals showed a waveform but played silent.
    try {
      if (this.tracksRef && Array.isArray(this.tracksRef)) {
        this.scheduleAudioClips(this.tracksRef);
      }
    } catch { /* noop */ }
  }

  disposeAudioClip(trackId, clipId) {
    const player = this.audioClipPlayers?.[trackId]?.[clipId];
    if (player) {
      try { player.dispose(); } catch {}
      delete this.audioClipPlayers[trackId][clipId];
    }
  }

  scheduleAudioClips(tracks) {
    // Clear existing audio schedules
    if (this.audioClipSchedules) {
      this.audioClipSchedules.forEach((id) => { try { Tone.getTransport().clear(id); } catch {} });
    }
    this.audioClipSchedules = [];
    const secPerBar = (60 / this.bpmRef) * 4;

    tracks.forEach((t) => {
      if (t.type !== "audio") return;
      if (t.muted) return;
      const hasSoloed = tracks.some((tr) => tr.solo);
      if (hasSoloed && !t.solo) return;
      (t.audioClips || []).forEach((clip) => {
        const player = this.audioClipPlayers?.[t.id]?.[clip.id];
        if (!player || !player.loaded) return;
        const startSec = (clip.bar || 0) * secPerBar;
        const offsetSec = Math.max(0, clip.offset || 0);
        const durationSec = Math.max(0.01, (clip.length || 1) * secPerBar);
        // v19.24 — Time-stretch. When `clip.stretched === true`, the
        // clip's visual `length` no longer matches its natural audio
        // duration — the whole sample must map into that new duration.
        // We set `player.playbackRate = natural_sec / new_sec` so a
        // 1s clip stretched to 2s plays at 0.5× speed (slower + longer).
        // Also increase the `duration` arg to `start()` so Tone.Player
        // stops at the END of the stretched playback rather than after
        // the natural-length window.
        //
        // Note: uses Web Audio's built-in playbackRate — pitch shifts
        // with speed (no pitch preservation library integrated). That's
        // the v1 acceptable trade-off called out in the spec.
        let effectiveRate = 1;
        let effectiveDuration = durationSec;
        if (clip.stretched && clip.duration && clip.duration > 0) {
          const naturalRemainingSec = Math.max(0.01, clip.duration - offsetSec);
          effectiveRate = naturalRemainingSec / durationSec;
          // Guard against pathological rates outside the sane 0.25×…4× band.
          effectiveRate = Math.max(0.1, Math.min(10, effectiveRate));
        }
        // v19.28 — Apply semitone pitch shift on top of any stretch rate.
        // `clip.pitch` is a semitone offset (integer). Playback rate
        // multiplier for a semitone offset is 2^(offset/12) — standard
        // musical detune. Combined with stretch: effectiveRate *=
        // pitchRate so a stretched-AND-pitched clip does both.
        // Non-destructive: original audio file untouched.
        if (Number.isFinite(clip.pitch) && clip.pitch !== 0) {
          const pitchRate = Math.pow(2, clip.pitch / 12);
          effectiveRate = effectiveRate * pitchRate;
        }
        // Final clamp so pitch + stretch combinations can't blow up the
        // AudioBufferSourceNode rate parameter into unusable territory.
        effectiveRate = Math.max(0.1, Math.min(16, effectiveRate));
        const id = Tone.getTransport().schedule((time) => {
          try {
            // v19.26 — Live-playback reverse. Tone.Player's `reverse`
            // setter reverses the underlying AudioBuffer in place.
            // We toggle it PER schedule tick so a producer can
            // reverse/un-reverse on the fly and hear it on the next
            // trigger without rebuilding the player.
            const wantReversed = !!clip.reversed;
            if (player.reverse !== wantReversed) {
              try { player.reverse = wantReversed; } catch {}
            }
            player.playbackRate = effectiveRate;
            // start(time, offset, duration) → Tone.Player automatically stops after `duration`.
            player.start(time, offsetSec, effectiveDuration);
          } catch {}
        }, startSec);
        this.audioClipSchedules.push(id);
      });
    });
  }

  async loadSampleToTrack(trackId, url, rootPitch = "C4") {
    await this.init();
    // Polyphonic Tone.Sampler: each triggered note plays its own voice (supports chords).
    // `rootPitch` is the note the sample is considered rooted at — Tone.Sampler
    // pitch-shifts from here for any other pitch requested. For drum one-shots
    // the caller ALSO sets track.samplerOneShot=true so the trigger side always
    // asks for this rootPitch (no pitch shift).
    //
    // RESILIENT DECODE PATH (iter 17): pre-decode the URL to an AudioBuffer
    // on the online AudioContext BEFORE handing it to Tone.Sampler. This lets
    // us surface a clean per-track error state on decode failure instead of
    // crashing the whole audio engine with an unhandled Tone.Buffer rejection
    // (the reported "unable to decode audio data" runtime error). The caller
    // gets a boolean result and can render an error banner + let the trigger
    // path silently fall back to the per-track Galaxy Synth voice.
    //
    // FRESH-REBUILD (iter 19): every call disposes the existing sampler and
    // creates a new one with ONLY the requested rootPitch mapped. Without
    // this, the default Salamander piano C4 mapping stays alive after the
    // user picks an 808 kit sound and Tone.Sampler picks the NEAREST mapped
    // root — often the wrong one, producing the reported "808 doesn't
    // pitch-shift" and "hear piano when I picked 808" bugs. Clean slate =
    // deterministic pitch mapping across the whole piano-roll spectrum.
    //
    // Returns: { ok: true } | { ok: false, reason: string }
    const buf = await decodeSampleUrl(url);

    // Always dispose the existing sampler so a clean rebuild happens whether
    // the decode succeeded (fresh mapping) or failed (fallback synth takes over).
    const stale = this.samplerPlayers[trackId];
    if (stale) {
      try { stale.dispose(); } catch { /* noop */ }
      delete this.samplerPlayers[trackId];
    }

    if (!buf) {
      // Ensure a fallback Galaxy Synth voice exists for this track so the
      // producer still gets audible playback while they re-upload.
      this._ensureSamplerFallback(trackId);
      return { ok: false, reason: "Sample corrupted — please re-upload" };
    }

    // Build a fresh Tone.Sampler with ONLY the requested root pitch mapped.
    // Route through the per-track Volume bus so the Mixer fader + mute switch
    // work; connectTrackChain — invoked by the next setupSequencer or the
    // caller — will re-wire this through any FX chain the track has.
    const sampler = new Tone.Sampler({ attack: 0.005, release: 0.2 });
    sampler.volume.value = -3;
    const bus = this.trackOutputs[trackId];
    sampler.connect(bus?.volume || this.effects.eq);
    this.samplerPlayers[trackId] = sampler;

    try {
      sampler.add(rootPitch, buf);
      return { ok: true };
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[AudioEngine.loadSampleToTrack] sampler.add threw", e);
      try { sampler.dispose(); } catch { /* noop */ }
      delete this.samplerPlayers[trackId];
      this._ensureSamplerFallback(trackId);
      return { ok: false, reason: "Sample load failed — please re-upload" };
    }
  }

  // Lazy Galaxy Synth fallback for sampler tracks whose sample failed to
  // decode. Triggered by the sampler branches in triggerTrackInRegion /
  // triggerTrack / audition when `track.samplerLoadError` is set OR when the
  // Tone.Sampler simply has no buffer loaded. Kept identical to the Galaxy
  // Synth voice used by regular synth tracks so the producer's mix stays
  // musical while they re-upload the broken sample. Routes through the
  // per-track Volume bus (iter 19) so mixer fader + mute still work while
  // the fallback is active.
  _ensureSamplerFallback(trackId) {
    if (this.samplerFallbackSynths[trackId]) return this.samplerFallbackSynths[trackId];
    const synth = new Tone.PolySynth(Tone.Synth, {
      oscillator: { type: "fatsawtooth", count: 3, spread: 30 },
      envelope: { attack: 0.02, decay: 0.25, sustain: 0.55, release: 0.9 },
    });
    synth.volume.value = -10;
    const bus = this.trackOutputs[trackId];
    synth.connect(bus?.volume || this.effects.eq);
    this.samplerFallbackSynths[trackId] = synth;
    return synth;
  }

  // Trigger a track at a given Tone time
  triggerTrack(track, step, time, hasSoloed) {
    if (!track || !track.type) return;
    if (track.muted) return;
    if (hasSoloed && !track.solo) return;

    const vol = track.volume ?? 0.8;
    const vel = Math.max(0.05, Math.min(1, vol));

    // Per-track drum instance routes through the track's volume bus + meter.
    // We fall back to the shared synth only if the per-track instance hasn't
    // been built yet (race during first triggerTrack call).
    const drumKick   = this.drumInstances[track.id] && track.type === "kick"   ? this.drumInstances[track.id] : this.drumSynths.kick;
    const drumSnare  = this.drumInstances[track.id] && track.type === "snare"  ? this.drumInstances[track.id] : this.drumSynths.snare;
    const drumHihat  = this.drumInstances[track.id] && track.type === "hihat"  ? this.drumInstances[track.id] : this.drumSynths.hihat;
    const drumClap   = this.drumInstances[track.id] && track.type === "clap"   ? this.drumInstances[track.id] : this.drumSynths.clap;

    if (track.type === "kick") {
      if (track.pattern[step]) drumKick.triggerAttackRelease("C1", "8n", time, vel);
    } else if (track.type === "snare") {
      if (track.pattern[step]) drumSnare.triggerAttackRelease("16n", time, vel);
    } else if (track.type === "hihat") {
      if (track.pattern[step]) drumHihat.triggerAttackRelease("C4", "32n", time, vel * 0.7);
    } else if (track.type === "clap") {
      if (track.pattern[step]) drumClap.triggerAttackRelease("16n", time, vel);
    } else if (track.type === "sampler") {
      const sampler = this.samplerPlayers[track.id];
      const samplerReady = sampler && (sampler.loaded !== false) && !track.samplerLoadError;
      const notesAtStep = (track.notes || []).filter((n) => n.step === step);
      const secPerStep = 60 / this.bpmRef / 4;
      const oneShot = !!track.samplerOneShot;
      const rootPitch = track.samplerRootPitch || "C4";
      const fallback = !samplerReady ? this.samplerFallbackSynths[track.id] : null;
      if (notesAtStep.length > 0) {
        notesAtStep.forEach((n) => {
          const durSec = Math.max(0.05, (n.duration || 1) * secPerStep);
          const firePitch = oneShot ? rootPitch : n.pitch;
          if (samplerReady) {
            try { sampler.triggerAttackRelease(firePitch, durSec, time, vel); } catch { /* ignore */ }
          } else if (fallback) {
            try { fallback.triggerAttackRelease(firePitch, durSec, time, vel); } catch { /* ignore */ }
          }
        });
      } else if (track.pattern[step]) {
        if (samplerReady) {
          try { sampler.triggerAttackRelease(rootPitch, secPerStep, time, vel); } catch { /* ignore */ }
        } else if (fallback) {
          try { fallback.triggerAttackRelease(rootPitch, secPerStep, time, vel); } catch { /* ignore */ }
        }
      }
    } else if (track.type === "synth") {
      const synth = this.synthVoices[track.id];
      if (!synth) return;
      const notesAtStep = track.notes.filter((n) => n.step === step);
      const secPerStep = 60 / this.bpmRef / 4;
      notesAtStep.forEach((n) => {
        const durSec = Math.max(0.05, (n.duration || 1) * secPerStep);
        synth.triggerAttackRelease(n.pitch, durSec, time, vel);
      });
    }
  }

  setupSequencer(tracks, bpm, regions = []) {
    // If a schedule is already running and only the tracksRef/regionsRef
    // content changed (notes added, mute toggled, region moved), keep the
    // running Transport.scheduleRepeat alive and just update the refs it
    // reads inside the tick callback. Disposing + recreating the schedule
    // during active playback consistently left the new one dormant — see
    // iter 20 RCA. This live-update path fixes "MIDI regions silent while
    // laying down notes" for good.
    const isRunning = Tone.getTransport().state === "started" && this.sequenceScheduleId != null;
    if (isRunning) {
      this.tracksRef = tracks;
      this.regionsRef = regions;
      this.bpmRef = bpm || this.bpmRef;
      // Re-ensure per-track instruments/outputs in case a new track was
      // added mid-play; existing ones are left alone.
      tracks.forEach((t) => {
        this.ensureTrackOutput(t);
        this.ensureTrackInstrument(t);
      });
      // Also re-schedule audio clips (they use Transport.schedule, not the
      // Tone.Sequence, so this is safe to re-do without breaking playback).
      this.scheduleAudioClips(tracks);
      return;
    }

    this.disposeSequence();
    // v21.2 — DOUBLE-TRIGGER STUTTER FIX (Feb 2026). Belt-and-braces:
    // wipe EVERY event on the shared Tone.Transport (not just the ids
    // this engine tracked) before we register a fresh scheduleRepeat.
    // The GenPlayer, the Beat Interchanger preview, and any React
    // StrictMode double-mount can leave orphan repeats on the same
    // Transport singleton; if they survive into the DAW cold-start,
    // producers hear every drum hit twice. `Transport.cancel(0)`
    // evicts all scheduled events at time 0 and beyond — the canonical
    // Tone.js "reset the timeline" operation.
    try { Tone.getTransport().cancel(0); } catch { /* noop */ }
    this.tracksRef = tracks;
    this.regionsRef = regions;
    this.bpmRef = bpm || this.bpmRef;
    // CRITICAL: keep Tone.Transport in sync with the project tempo so any
    // Transport-driven scheduling (loop length, tick visualizer, future
    // metronome) plays back at the BPM the user actually selected. Without
    // this, the transport stays at the init-time default (120) while bpmRef
    // moves — manifesting to the user as a "140 BPM project sounds wrong"
    // tempo drift.
    if (this.initialized) {
      Tone.getTransport().bpm.value = this.bpmRef;
    }
    if (!this.initialized) return;

    // ensure per-track instruments exist + rebuild FX chains
    tracks.forEach((t) => {
      this.ensureTrackInstrument(t);
      this.connectTrackChain(t);
    });

    // Migrated from Tone.Sequence to Tone.Transport.scheduleRepeat (iter 22)
    // because Tone.Sequence.start() never bound to Transport in Tone.js
    // v15.1.22 within this app's context — verified across iters 19-21 that
    // sequence.progress stays NaN and the callback never fires, while a
    // scheduleRepeat call in the same page fires reliably. scheduleRepeat
    // is the same underlying primitive Tone.Sequence uses internally, so
    // no functional loss — we compute the step index ourselves from the
    // Transport ticks (192 PPQ / 4 = 48 ticks per 16th note by default).
    const PPQ = Tone.getTransport().PPQ || 192;
    const ticksPer16th = PPQ / 4;
    this.sequenceScheduleId = Tone.getTransport().scheduleRepeat((time) => {
      // Defensive try/catch: any silent throw in the callback body (bad
      // Draw.schedule, getTicksAtTime failure, triggerTrackInRegion bug on
      // exotic track state) will otherwise kill the entire schedule with no
      // stack trace. Logging keeps subsequent ticks firing.
      try {
        const ticks = Tone.getTransport().getTicksAtTime(time);
        const absStep = Math.floor(ticks / ticksPer16th);
        // v17.3 ABSOLUTE PLAYBACK FIX — the visual `currentStep` mod-16
        // is only used for UI feedback (pattern-view step lamps); the
        // trigger scheduler now passes the ABSOLUTE step and lets
        // triggerTrackInRegion compute a region-relative offset. The
        // previous `step = abs % 16` collapsed every bar into bar 1,
        // so multi-bar regions (and any region past bar 1) always
        // fetched notes from the first 16 steps.
        const uiStep = absStep % 16;
        this.currentStep = uiStep;
        this.stepListeners.forEach((cb) => Tone.Draw.schedule(() => cb(uiStep), time));
        const barFloat = ticks / (PPQ * 4);
        const currentHasSoloed = this.tracksRef.some((t) => t.solo);

        this.tracksRef.forEach((t) => {
          if (t.type === "audio") return;
          const activeRegion = this.regionsRef.find((r) =>
            r.trackId === t.id && r.bar <= barFloat + 1e-6 && r.bar + r.length > barFloat + 1e-6
          );
          if (!activeRegion) return;
          // Region-relative step index (0..region.length*16-1). Notes
          // and pattern arrays in the store are indexed relative to
          // region start, so this is the correct lookup key.
          const regionStep = absStep - Math.round((activeRegion.bar || 0) * 16);
          this.triggerTrackInRegion(t, activeRegion, regionStep, time, currentHasSoloed);
        });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("[AudioEngine schedule tick]", e);
      }
    }, "16n", 0);
    // Keep a `.sequence`-shaped object exposed for backwards-compat with any
    // outside code that still reads engine.sequence.state / .progress, and
    // for the diagnostics dump in engine.debug().
    this.sequence = { scheduleId: this.sequenceScheduleId, state: "scheduled" };

    this.scheduleAudioClips(tracks);
  }

  triggerTrackInRegion(track, region, step, time, hasSoloed) {
    if (!track || track.muted) return;
    if (hasSoloed && !track.solo) return;

    const vol = track.volume ?? 0.8;
    const vel = Math.max(0.05, Math.min(1, vol));
    const pattern = region.pattern || [];
    const notes = region.notes || [];

    const drumI = this.drumInstances[track.id];

    // When a drum track was "Convert to Piano Roll"-ed, fire from notes (with
    // sub-step offsets so the user gets 1/64 resolution) instead of the
    // 16-step pattern.
    const drumTypes = ["kick", "snare", "hihat", "clap"];
    if (drumTypes.includes(track.type) && track.viewMode === "pr") {
      const notesAtStep = notes.filter((n) => Math.floor(n.step) === step);
      if (notesAtStep.length === 0) return;
      const secPerStep = 60 / this.bpmRef / 4;
      notesAtStep.forEach((n) => {
        const off = (n.step - step) * secPerStep;
        const t = time + off;
        if (track.type === "kick") {
          if (!drumKitManager.trigger("kick", t, vel)) (drumI || this.drumSynths.kick).triggerAttackRelease("C1", "8n", t, vel);
        } else if (track.type === "snare") {
          if (!drumKitManager.trigger("snare", t, vel)) (drumI || this.drumSynths.snare).triggerAttackRelease("16n", t, vel);
        } else if (track.type === "hihat") {
          if (!drumKitManager.trigger("hihat", t, vel * 0.7)) (drumI || this.drumSynths.hihat).triggerAttackRelease("C4", "32n", t, vel * 0.7);
        } else if (track.type === "clap") {
          if (!drumKitManager.trigger("clap", t, vel)) (drumI || this.drumSynths.clap).triggerAttackRelease("16n", t, vel);
        }
      });
      return;
    }

    if (track.type === "kick") {
      if (pattern[step]) {
        if (!drumKitManager.trigger("kick", time, vel)) (drumI || this.drumSynths.kick).triggerAttackRelease("C1", "8n", time, vel);
        // Sidechain: Kick → 808 (bass tracks duck by -12dB, 5ms/100ms).
        this._scheduleSidechainDuck("bass", time);
      }
    } else if (track.type === "snare") {
      if (pattern[step]) {
        if (!drumKitManager.trigger("snare", time, vel)) (drumI || this.drumSynths.snare).triggerAttackRelease("16n", time, vel);
      }
    } else if (track.type === "hihat") {
      if (pattern[step]) {
        if (!drumKitManager.trigger("hihat", time, vel * 0.7)) (drumI || this.drumSynths.hihat).triggerAttackRelease("C4", "32n", time, vel * 0.7);
      }
    } else if (track.type === "clap") {
      if (pattern[step]) {
        if (!drumKitManager.trigger("clap", time, vel)) (drumI || this.drumSynths.clap).triggerAttackRelease("16n", time, vel);
      }
    } else if (track.type === "sampler") {
      // Off-grid support: notes whose floor(step) matches this step trigger here,
      // with a sub-step time offset (n.step - step) * secPerStep.
      const notesAtStep = notes.filter((n) => Math.floor(n.step) === step);
      const secPerStep = 60 / this.bpmRef / 4;
      const pads = track.samplerPads || [];

      // 1) Pad-based playback: if any note's pitch matches a pad with a loaded sample, play that pad
      const triggered = new Set();
      notesAtStep.forEach((n) => {
        const matchPad = pads.find((p) => p.pitch === n.pitch && p.sampleUrl);
        if (matchPad) {
          const player = this.samplerPadPlayers?.[track.id]?.[matchPad.id];
          if (player && player.loaded) {
            try {
              const subOffset = (n.step - step) * secPerStep;
              player.playbackRate = 1;
              player.start(time + subOffset);
            } catch {}
          }
          triggered.add(n.id);
        }
      });

      // 2) Polyphonic Tone.Sampler for any unmatched notes (handles chords + melodies)
      const remaining = notesAtStep.filter((n) => !triggered.has(n.id));
      const sampler = this.samplerPlayers[track.id];
      const samplerReady = sampler && (sampler.loaded !== false);
      // Drum-one-shot mode: sampler always fires at its native root pitch
      // regardless of the note being played. Producers pick "Kick" from the
      // Kit Sound Library and want it to sound like a kick every time, not
      // pitch-shifted by the piano roll.
      const oneShot = !!track.samplerOneShot;
      const rootPitch = track.samplerRootPitch || "C4";
      // Fallback synth: used when the track's sample failed to decode OR when
      // no sample is loaded at all but the piano-roll has notes. Keeps the
      // producer productive while they re-upload a broken sample.
      const fallback = (track.samplerLoadError || !samplerReady)
        ? this.samplerFallbackSynths[track.id]
        : null;
      if (samplerReady && !track.samplerLoadError && remaining.length > 0) {
        remaining.forEach((n) => {
          const durSec = Math.max(0.05, (n.duration || 1) * secPerStep);
          const subOffset = (n.step - step) * secPerStep;
          try {
            sampler.triggerAttackRelease(oneShot ? rootPitch : n.pitch, durSec, time + subOffset, vel);
          } catch { /* ignore per-note failures */ }
        });
      } else if (fallback && remaining.length > 0) {
        remaining.forEach((n) => {
          const durSec = Math.max(0.05, (n.duration || 1) * secPerStep);
          const subOffset = (n.step - step) * secPerStep;
          try {
            fallback.triggerAttackRelease(oneShot ? rootPitch : n.pitch, durSec, time + subOffset, vel);
          } catch { /* ignore */ }
        });
      } else if (notesAtStep.length === 0 && pattern[step]) {
        if (samplerReady && !track.samplerLoadError) {
          try { sampler.triggerAttackRelease(rootPitch, secPerStep, time, vel); } catch { /* ignore */ }
        } else if (fallback) {
          try { fallback.triggerAttackRelease(rootPitch, secPerStep, time, vel); } catch { /* ignore */ }
        }
      }
    } else if (track.type === "bass") {
      // Strict routing: bass tracks ALWAYS go through the admin's Bass Kit
      // sampler when one is loaded, otherwise fall back to the per-track
      // MonoSynth voice. They never touch the Melody Kit or drum slots.
      const synth = this.synthVoices[track.id];
      const notesAtStep = notes.filter((n) => Math.floor(n.step) === step);
      const secPerStep = 60 / this.bpmRef / 4;
      notesAtStep.forEach((n) => {
        const durSec = Math.max(0.05, (n.duration || 1) * secPerStep);
        const subOffset = (n.step - step) * secPerStep;
        // Sidechain: 808 → Piano (melody tracks duck when this bass note fires).
        this._scheduleSidechainDuck("melody", time + subOffset);
        if (bassKitManager.trigger(n.pitch, time + subOffset, durSec, vel)) return;
        if (synth) synth.triggerAttackRelease(n.pitch, durSec, time + subOffset, vel);
      });
    } else if (track.type === "melody") {
      // Strict routing: melody tracks ALWAYS go through the admin's Melody
      // Kit priority sampler (piano → lead → brass → strings → flute) when one
      // is loaded, otherwise fall back to the per-track PolySynth lead voice.
      const synth = this.synthVoices[track.id];
      const notesAtStep = notes.filter((n) => Math.floor(n.step) === step);
      const secPerStep = 60 / this.bpmRef / 4;
      notesAtStep.forEach((n) => {
        const durSec = Math.max(0.05, (n.duration || 1) * secPerStep);
        const subOffset = (n.step - step) * secPerStep;
        if (melodyKitManager.trigger(n.pitch, time + subOffset, durSec, vel)) return;
        if (synth) synth.triggerAttackRelease(n.pitch, durSec, time + subOffset, vel);
      });
    } else if (track.type === "synth") {
      const synth = this.synthVoices[track.id];
      if (!synth) return;
      // Off-grid support: same scheme as sampler — match floor(step), apply sub-step offset.
      const notesAtStep = notes.filter((n) => Math.floor(n.step) === step);
      const secPerStep = 60 / this.bpmRef / 4;
      // Legacy fallback: a Galaxy Synth track named "bass"/"lead"/"melody"/"piano"
      // routes through the matching kit. New projects should use the dedicated
      // "bass" / "melody" instrument types instead so the routing is explicit
      // and name-independent.
      const nameL = (track.name || "").toLowerCase();
      const isBass = nameL.includes("bass");
      const isLead = nameL.includes("lead") || nameL.includes("melody") || nameL.includes("piano");
      notesAtStep.forEach((n) => {
        const durSec = Math.max(0.05, (n.duration || 1) * secPerStep);
        const subOffset = (n.step - step) * secPerStep;
        if (isBass && bassKitManager.trigger(n.pitch, time + subOffset, durSec, vel)) return;
        if (isLead && melodyKitManager.trigger(n.pitch, time + subOffset, durSec, vel)) return;
        synth.triggerAttackRelease(n.pitch, durSec, time + subOffset, vel);
      });
    }
  }

  pitchToSemis(pitch, ref = "C4") {
    const names = { C: 0, "C#": 1, D: 2, "D#": 3, E: 4, F: 5, "F#": 6, G: 7, "G#": 8, A: 9, "A#": 10, B: 11 };
    const parse = (p) => {
      const m = p.match(/^([A-G]#?)(-?\d+)$/);
      if (!m) return 0;
      return names[m[1]] + (parseInt(m[2], 10) + 1) * 12;
    };
    return parse(pitch) - parse(ref);
  }

  disposeSequence() {
    // v15.0 — Belt-and-suspenders: also cancel ALL schedule ids on the
    // Transport, not just our scheduleRepeat. `scheduleAudioClips`
    // registers Transport.schedule ids for each clip and tracks them in
    // `audioClipSchedules` — clearing them here guards against a stale
    // audio-clip schedule surviving a rebuild and double-triggering.
    if (this.sequenceScheduleId != null) {
      try { Tone.getTransport().clear(this.sequenceScheduleId); } catch { /* noop */ }
      this.sequenceScheduleId = null;
    }
    if (Array.isArray(this.audioClipSchedules)) {
      this.audioClipSchedules.forEach((id) => { try { Tone.getTransport().clear(id); } catch { /* noop */ } });
      this.audioClipSchedules = [];
    }
    // Backwards-compat: legacy Tone.Sequence path (any lingering .dispose() call).
    if (this.sequence && typeof this.sequence.dispose === "function") {
      try { this.sequence.stop(); this.sequence.dispose(); } catch { /* noop */ }
    }
    this.sequence = null;
  }

  // Called from dawStore.hydrateFromProject after a saved project loads.
  // The engine keeps per-track caches (synthVoices, samplerPlayers, fallback
  // synths, trackOutputs, audioClipPlayers) keyed by trackId. When a project
  // hydrates with brand-new track ids those caches are orphaned and the new
  // tracks have NO wired instruments — Play triggers into the void ("silent
  // session on reload"). This helper wipes every cache, then re-ensures each
  // hydrated track's output bus + instrument, and re-loads any kit-sourced
  // sampler sample URLs so the sampler is buffer-ready before the first Play.
  async rebuildAllForProject(tracks) {
    if (!this.initialized) return;
    // Stop any running transport so we don't fire into half-torn-down instruments.
    try { Tone.getTransport().stop(); } catch { /* noop */ }
    this.disposeSequence();
    // Dispose every cached per-track resource.
    for (const trackId of Object.keys(this.synthVoices)) {
      try { this.synthVoices[trackId].dispose(); } catch { /* noop */ }
    }
    this.synthVoices = {};
    for (const trackId of Object.keys(this.samplerPlayers)) {
      try { this.samplerPlayers[trackId].dispose(); } catch { /* noop */ }
    }
    this.samplerPlayers = {};
    for (const trackId of Object.keys(this.samplerFallbackSynths)) {
      try { this.samplerFallbackSynths[trackId].dispose(); } catch { /* noop */ }
    }
    this.samplerFallbackSynths = {};
    for (const trackId of Object.keys(this.trackOutputs)) {
      try {
        const { volume, meter, ducker, panner } = this.trackOutputs[trackId] || {};
        try { ducker?.dispose(); } catch { /* noop */ }
        try { volume?.dispose(); } catch { /* noop */ }
        try { panner?.dispose(); } catch { /* noop */ }
        try { meter?.dispose(); } catch { /* noop */ }
      } catch { /* noop */ }
    }
    this.trackOutputs = {};
    for (const trackId of Object.keys(this.audioClipPlayers || {})) {
      const bucket = this.audioClipPlayers[trackId] || {};
      for (const cid of Object.keys(bucket)) {
        try { bucket[cid].dispose(); } catch { /* noop */ }
      }
    }
    this.audioClipPlayers = {};
    // Rebuild each track's audio graph from scratch.
    for (const t of tracks || []) {
      this.ensureTrackOutput(t);
      this.ensureTrackInstrument(t);
      // Re-load any kit-sourced sampler URL (blob: URLs are already stripped
      // on save per user choice (b), so surviving sampleUrls point to
      // /api/static/... backend paths and decode cleanly).
      if (t.type === "sampler" && t.sampleUrl && !t.sampleUrl.startsWith("blob:")) {
        try { await this.loadSampleToTrack(t.id, t.sampleUrl, t.samplerRootPitch || "C4"); } catch (e) {
          // eslint-disable-next-line no-console
          console.warn("[rebuildAllForProject] sampler reload failed", t.id, e);
        }
      }
    }
    // Ensure the AudioContext is running so the very first Play click after a
    // reload isn't blocked by autoplay policy.
    await this.ensureRunning();
  }

  onStep(cb) { this.stepListeners.add(cb); return () => this.stepListeners.delete(cb); }

  // Resume the AudioContext on every user gesture. `Tone.start()` is idempotent
  // and safe to call repeatedly — it only calls context.resume() if the
  // context is still 'suspended'. This is the critical unlock: `engine.init()`
  // is invoked from a component mount effect (no user gesture) so the initial
  // `Tone.start()` inside init() gets blocked by the browser autoplay policy
  // and the context stays suspended forever. Without this second unlock,
  // `Tone.Transport` runs (JS-timer-driven, no audio needed) so the playhead
  // animates, but every scheduled note fires into a suspended graph — zero
  // audible output. Reported bug: "playhead moves but no sound".
  async ensureRunning() {
    try {
      await Tone.start();
      const ctx = Tone.getContext()?.rawContext;
      if (ctx && ctx.state !== "running") {
        await ctx.resume().catch(() => {});
      }
    } catch { /* noop */ }
  }

  /**
   * v19.23 — Wait for every audio buffer referenced by the current
   * session to be fully decoded and playable before we let the
   * transport start. Guarantees the first-beat 808/kick/snare never
   * gets silently skipped because its Tone.Player was still async-
   * decoding when the scheduler fired.
   *
   * Strategy:
   *   1. `Tone.loaded()` — Tone.js's built-in "all buffers ready"
   *      promise. Fastest path for anything that went through the
   *      standard Tone.Player(url) / Tone.Sampler({urls}) pipeline.
   *   2. Per-track scan — walk every drum/sampler/audio-clip player
   *      map we've built and await any Buffer that still reports
   *      `.loaded === false`. Catches players constructed via
   *      `new Tone.Player(existingBuffer)` where step 1 already
   *      resolved before the player was even created.
   *   3. Bounded wait — cap total wait at 2 s so a broken/404'd
   *      sample can't hang playback forever. Anything still loading
   *      past the cap falls back to the scheduler's existing
   *      graceful-skip path.
   */
  async _awaitAllBuffersReady() {
    const CAP_MS = 2000;
    const cap = new Promise((r) => setTimeout(r, CAP_MS));
    try { await Promise.race([Tone.loaded(), cap]); } catch { /* ignore */ }
    const waitFor = (buf) => new Promise((resolve) => {
      if (!buf || buf.loaded) { resolve(); return; }
      const start = performance.now();
      const tick = () => {
        if (buf.loaded || performance.now() - start > CAP_MS) resolve();
        else setTimeout(tick, 20);
      };
      tick();
    });
    const buffers = [];
    try {
      Object.values(this.drumPlayers || {}).forEach((byTrack) => {
        Object.values(byTrack || {}).forEach((p) => { if (p && p.buffer) buffers.push(p.buffer); });
      });
    } catch {}
    try {
      Object.values(this.samplerPlayers || {}).forEach((s) => {
        if (s && s.buffer) buffers.push(s.buffer);
      });
    } catch {}
    try {
      Object.values(this.audioPlayers || {}).forEach((byClip) => {
        Object.values(byClip || {}).forEach((p) => { if (p && p.buffer) buffers.push(p.buffer); });
      });
    } catch {}
    if (buffers.length > 0) {
      await Promise.race([Promise.all(buffers.map(waitFor)), cap]);
    }
  }


  async play(opts = {}) {
    // v21.2 — DOUBLE-TRIGGER STUTTER FIX (Feb 2026).
    // Under async races (rapid Play/Space taps, or React StrictMode
    // re-invoking effects), two `engine.play()` calls could enter the
    // function before `Tone.getTransport().state` flipped to "started",
    // so BOTH would sail past the `if (state === "started") return`
    // guard and call `t.start()` — producing every note ~2×. The
    // Promise-lock below serialises concurrent calls so only the first
    // one runs the start path; every follower awaits the same result.
    if (this._playInFlight) return this._playInFlight;
    this._playInFlight = (async () => {
      await this.init();
      await this.ensureRunning();
      // v19.23 — Pre-flight buffer gate. Before starting the transport
      // we wait for every audio buffer in the current session to be
      // fully decoded and resident. Previously ~1 in 8 playbacks would
      // silently drop a single drum / 808 / sampler hit because the
      // Tone.Player for that slot was still async-decoding when the
      // scheduler fired its first note. `Tone.loaded()` resolves only
      // when EVERY Tone.Buffer created via `Tone.Player(url)` /
      // `Tone.Sampler({url})` is done — the canonical Tone.js gate. We
      // also check every tracked drum/sampler player individually as a
      // belt-and-suspenders defense against players constructed after
      // the last Tone.loaded() barrier resolved. Adds ~50–200 ms on the
      // FIRST play (Chrome typically decodes short one-shots in <100 ms)
      // and 0 ms on subsequent plays because buffers are already
      // resident. A tiny worthwhile trade for zero missed hits.
      await this._awaitAllBuffersReady();
      const t = Tone.getTransport();
      // v15.0 STUTTER FIX — idempotent play.
      if (t.state === "started") return;
      // v17.1 DAW-LOOP RESET — the GenPlayer / Beat Interchanger shares
      // the same global Tone.Transport singleton and hard-locks a 1-bar
      // loop. Without this guard the DAW inherits that loop and only
      // plays bar 1 forever. Arranger sets `_dawLoopActive = true` when
      // the user draws a blue Loop Region; otherwise we unconditionally
      // clear loop state so the transport advances through the whole
      // arrangement.
      if (!this._dawLoopActive) {
        try { t.loop = false; } catch {}
      }
      // v19.20 — RESOLVE THE ACTUAL START POSITION for this play call.
      let startSec = null;
      if (t.loop) {
        try {
          startSec = Tone.Time(t.loopStart).toSeconds();
        } catch { /* time-conversion failure — leave transport as-is */ }
      } else if (typeof opts.startSec === "number" && opts.startSec >= 0) {
        startSec = opts.startSec;
      }
      if (startSec !== null) {
        try { t.seconds = startSec; } catch {}
      }
      if (startSec !== null) {
        t.start(undefined, startSec);
      } else {
        t.start();
      }
    })().finally(() => { this._playInFlight = null; });
    return this._playInFlight;
  }

  // v17.1 — Arranger calls this whenever the user drags / clears the
  // blue Loop Region. When null → engine.play() will disable loop; when
  // {start, end} → engine keeps the loop points and toggles loop on.
  setDawLoop(region) {
    const t = Tone.getTransport();
    if (region && typeof region.start === "number" && typeof region.end === "number" && region.end > region.start) {
      this._dawLoopActive = true;
      try {
        t.loop = true;
        t.loopStart = region.start * (60 / (t.bpm.value || 120)) * 4;
        t.loopEnd = region.end * (60 / (t.bpm.value || 120)) * 4;
      } catch {}
    } else {
      this._dawLoopActive = false;
      try { t.loop = false; } catch {}
    }
  }

  pause() {
    Tone.getTransport().pause();
    // Complete pause: Tone.Player buffers, sampler voices, and synth voices live
    // on the AudioContext clock (not Transport) once started, so we must explicitly
    // silence them to truly halt all audio output.
    try {
      Object.values(this.audioClipPlayers || {}).forEach((trk) => {
        Object.values(trk || {}).forEach((p) => { try { p.stop(); } catch {} });
      });
      Object.values(this.samplerPadPlayers || {}).forEach((pads) => {
        Object.values(pads || {}).forEach((p) => { try { p.stop(); } catch {} });
      });
      Object.values(this.samplerPlayers || {}).forEach((s) => {
        try { s.releaseAll(); } catch {}
      });
      Object.values(this.synthVoices || {}).forEach((s) => {
        try { s.releaseAll(); } catch {}
      });
    } catch {}
  }

  stop() {
    Tone.getTransport().stop();
    this.currentStep = -1;
    this.stepListeners.forEach((cb) => cb(-1));
    // v16.0 — SILENCE INSTANTANEOUSLY on stop. Previously only pause()
    // halted the Tone.Player / Sampler voices; stop() only stopped the
    // Transport, so audio-track clips + sampler pads that were mid-
    // playback would ring on until they hit their natural end. Now
    // stop() explicitly halts every live audio source across the DAW.
    try {
      Object.values(this.audioClipPlayers || {}).forEach((trk) => {
        Object.values(trk || {}).forEach((p) => { try { p.stop(); } catch {} });
      });
      Object.values(this.samplerPadPlayers || {}).forEach((pads) => {
        Object.values(pads || {}).forEach((p) => { try { p.stop(); } catch {} });
      });
      Object.values(this.samplerPlayers || {}).forEach((s) => {
        try { s.releaseAll(); } catch {}
      });
      Object.values(this.synthVoices || {}).forEach((s) => {
        try { s.releaseAll(); } catch {}
      });
      // Drum synth instances live on this.drumInstances (kick / snare / hihat / clap)
      Object.values(this.drumInstances || {}).forEach((d) => {
        try { d.releaseAll?.(); } catch {}
      });
    } catch {}
  }

  setBpm(bpm) {
    // v14.0 — clamp to sane DAW range so a typo like "1540" never
    // sets Transport into a 2× halftime feel or explodes the sequencer.
    const clamped = Math.max(40, Math.min(300, Number(bpm) || this.bpmRef || 120));
    Tone.getTransport().bpm.value = clamped;
    this.bpmRef = clamped;
    // v9.9.8 — recompute every BPM-synced delay's delayTime against the
    // new tempo. Iterates trackFxNodes populated by connectTrackChain so
    // producers hear the delay lock to the new BPM instantly.
    this.resyncDelays();
  }

  // Walks every FX node attached to a track chain and, for delays that
  // carry a `_syncNote` marker (set by createFxNode/applyFxParams), sets
  // delayTime to the seconds value of that notation at the current BPM.
  resyncDelays() {
    const fxByTrack = this.trackFx || {};
    for (const trackId of Object.keys(fxByTrack)) {
      const nodesById = fxByTrack[trackId] || {};
      for (const n of Object.values(nodesById)) {
        if (n && n._syncNote && n.delayTime) {
          try { n.delayTime.value = Tone.Time(n._syncNote).toSeconds(); } catch { /* noop */ }
        }
      }
    }
  }

  getSeconds() { return Tone.getTransport().seconds; }
  getBpm() { return Tone.getTransport().bpm.value; }

  setMasterVolume(v) {
    if (this.masterGain) this.masterGain.gain.rampTo(v, 0.05);
  }

  setEffect(name, param, value) {
    const fx = this.effects[name];
    if (!fx) return;
    if (param === "wet" && fx.wet) fx.wet.value = value;
    else if (fx[param]?.value !== undefined) fx[param].value = value;
    else if (typeof fx[param] !== "undefined") fx[param] = value;
  }

  // Audition: play a one-shot for a track regardless of pattern
  audition(track, pitch = "C4") {
    if (!this.initialized || !track) return;
    // Unlock the audio context in case this is the very first user gesture
    // (e.g. producer clicks a piano-roll cell before ever hitting Play).
    // Fire-and-forget — ensureRunning is idempotent and no-ops when running.
    this.ensureRunning();
    const now = Tone.now();
    const vel = track.volume ?? 0.8;
    if (track.type === "kick") {
      if (!drumKitManager.trigger("kick", now, vel)) this.drumSynths.kick.triggerAttackRelease("C1", "8n", now, vel);
    } else if (track.type === "snare") {
      if (!drumKitManager.trigger("snare", now, vel)) this.drumSynths.snare.triggerAttackRelease("16n", now, vel);
    } else if (track.type === "hihat") {
      if (!drumKitManager.trigger("hihat", now, vel)) this.drumSynths.hihat.triggerAttackRelease("C4", "32n", now, vel);
    } else if (track.type === "clap") {
      if (!drumKitManager.trigger("clap", now, vel)) this.drumSynths.clap.triggerAttackRelease("16n", now, vel);
    }
    else if (track.type === "sampler") {
      const sampler = this.samplerPlayers[track.id];
      const samplerReady = sampler && (sampler.loaded !== false) && !track.samplerLoadError;
      // Drum one-shot samples always audition at their root pitch (no shift).
      const auditionPitch = track.samplerOneShot ? (track.samplerRootPitch || "C4") : pitch;
      if (samplerReady) {
        try { sampler.triggerAttackRelease(auditionPitch, 0.4, undefined, vel); } catch { /* ignore */ }
      } else {
        const fallback = this.samplerFallbackSynths[track.id];
        if (fallback) { try { fallback.triggerAttackRelease(auditionPitch, "8n", undefined, vel); } catch { /* ignore */ } }
      }
    } else if (track.type === "bass") {
      if (bassKitManager.trigger(pitch, now, 0.4, vel)) return;
      const synth = this.synthVoices[track.id];
      if (synth) synth.triggerAttackRelease(pitch, "8n", now, vel);
    } else if (track.type === "melody") {
      if (melodyKitManager.trigger(pitch, now, 0.4, vel)) return;
      const synth = this.synthVoices[track.id];
      if (synth) synth.triggerAttackRelease(pitch, "8n", now, vel);
    } else if (track.type === "synth") {
      const synth = this.synthVoices[track.id];
      if (synth) synth.triggerAttackRelease(pitch, "8n", now, vel);
    }
  }

  async exportToWav(tracks, bpm, regions = [], totalBars = 4) {
    const stepsPerBar = 16;
    const secPerBar = (60 / bpm) * 4;
    const secondsPerStep = secPerBar / 16;

    // Determine total project duration to render
    let lastBar = 1;
    regions.forEach((r) => { lastBar = Math.max(lastBar, r.bar + r.length); });
    tracks.forEach((t) => (t.audioClips || []).forEach((c) => { lastBar = Math.max(lastBar, c.bar + c.length); }));
    const renderBars = Math.max(1, Math.ceil(lastBar));
    const duration = renderBars * secPerBar + 2; // tail

    // Snapshot the currently-loaded kit URLs and PRE-DECODE them to
    // AudioBuffers on the online context BEFORE entering Tone.Offline.
    // Rationale: the offline context's decodeAudioData is stricter than the
    // online one; a single failure inside Tone.Offline rejects `Tone.loaded()`
    // and tears down the entire render. Pre-decoding sidesteps the offline
    // decoder and isolates bad files per-lane via per-URL try/catch.
    const drumUrls = drumKitManager.getSampleUrls();
    const bassUrls = bassKitManager.getSampleUrls();
    const melodyMeta = melodyKitManager.getActiveSampleUrl();

    const drumBuffers = await decodeUrlMap(drumUrls);
    const bassBuffers = await decodeUrlMap(bassUrls);
    const melodyBuffer = melodyMeta ? await decodeSampleUrl(melodyMeta.url) : null;

    // Also pre-decode per-track sampler URLs (sampler tracks + audio clips
    // + sampler pads) so the exact same path is used for every uploaded
    // sample family. Missing / failing decode → null → synth fallback for
    // synth-typed tracks, or silent for audio/sampler tracks (matching
    // pre-fix behaviour).
    const trackSamplerBuffers = {};   // { trackId: AudioBuffer | null }
    const padBuffers = {};            // { trackId: { padId: AudioBuffer } }
    const audioClipBuffers = {};      // { trackId: { clipId: AudioBuffer } }
    for (const t of tracks) {
      if (t.type === "sampler") {
        if (t.sampleUrl) trackSamplerBuffers[t.id] = await decodeSampleUrl(t.sampleUrl);
        padBuffers[t.id] = {};
        for (const pad of (t.samplerPads || [])) {
          if (pad.sampleUrl) padBuffers[t.id][pad.id] = await decodeSampleUrl(pad.sampleUrl);
        }
      }
      if (t.type === "audio") {
        audioClipBuffers[t.id] = {};
        for (const clip of (t.audioClips || [])) {
          if (clip.url) audioClipBuffers[t.id][clip.id] = await decodeSampleUrl(clip.url);
        }
      }
    }

    const buffer = await Tone.Offline(async ({ transport }) => {
      // v9.9.10 — Set offline transport BPM FIRST so any FX that resolves
      // Tone.Time() notation (BPM-synced delay's "8n" → seconds) uses the
      // export BPM, not the online transport BPM. Previously the delay
      // ended up at the live-session BPM in the WAV.
      transport.bpm.value = bpm;
      // v9.7 — mirror the LIVE master chain 1:1: copy current EQ3 gains
      // from the online engine so exports respect any master EQ tweaks the
      // producer made before hitting Export.
      const eq = new Tone.EQ3({
        low: this.effects?.eq?.low?.value ?? 0,
        mid: this.effects?.eq?.mid?.value ?? 0,
        high: this.effects?.eq?.high?.value ?? 0,
      });
      const limiter = new Tone.Limiter(-1).toDestination();
      eq.connect(limiter);

      // v9.7 — per-track SERIAL CHAIN mirroring the live engine:
      //   voice → fxChain[0] → fxChain[1] → ... → panner → volume → master EQ
      // Previously the offline path connected every voice straight to the
      // master EQ, so any per-track FX (reverb, delay, EQ5, saturation,
      // etc.), custom volume, or Mixer pan was silently dropped from the
      // export. The wrapper below builds the same chain the live
      // connectTrackChain() builds and returns the CHAIN INPUT — the node
      // every voice for that track should connect to.
      const trackChainInputs = {};
      const trackFxNodes = {};   // dispose registry
      const buildTrackChain = (t) => {
        if (trackChainInputs[t.id]) return trackChainInputs[t.id];
        // Build FX nodes for this track (same factories the live engine uses).
        const fxNodes = (t.fxChain || []).map((fx) => {
          const node = this.createFxNode(fx.plugin);
          this.applyFxParams(node, fx.plugin, fx.params || {});
          return node;
        });
        const panner = new Tone.Panner(typeof t.pan === "number" ? t.pan : 0);
        const volume = new Tone.Volume(this.linearToDb(t.volume ?? 0.8));
        // Wire the tail: last fx (or the panner if no fx) → panner → volume → eq
        if (fxNodes.length === 0) {
          panner.connect(volume);
          volume.connect(eq);
          trackChainInputs[t.id] = panner;
        } else {
          // Chain fx0 → fx1 → ... → last → panner → volume → eq
          for (let i = 0; i < fxNodes.length - 1; i++) {
            fxNodes[i].connect(fxNodes[i + 1]);
          }
          fxNodes[fxNodes.length - 1].connect(panner);
          panner.connect(volume);
          volume.connect(eq);
          trackChainInputs[t.id] = fxNodes[0];
        }
        trackFxNodes[t.id] = { fxNodes, panner, volume };
        return trackChainInputs[t.id];
      };

      // ---- Drum voices: kit sample first (Tone.Player), synth fallback ---
      const drumPlayers = {};
      for (const slot of ["kick", "snare", "hihat", "clap", "openhat", "perc", "ride", "tom"]) {
        if (drumBuffers[slot]) {
          try { drumPlayers[slot] = new Tone.Player(drumBuffers[slot]); } catch { /* fall through */ }
        }
      }
      // Drum synth fallbacks — connect per-firing to the appropriate track
      // chain (see fireDrum below). Instantiated once, connected on trigger.
      const kickSynth = new Tone.MembraneSynth({
        pitchDecay: 0.05, octaves: 6,
        envelope: { attack: 0.001, decay: 0.4, sustain: 0.01, release: 1.4 },
      });
      const snareSynth = new Tone.NoiseSynth({ noise: { type: "white" }, envelope: { attack: 0.001, decay: 0.2, sustain: 0 } });
      const hihatSynth = new Tone.MetalSynth({
        frequency: 250, envelope: { attack: 0.001, decay: 0.1, release: 0.01 },
        harmonicity: 5.1, modulationIndex: 32, resonance: 4000, octaves: 1.5,
      });
      hihatSynth.volume.value = -15;
      const clapSynth = new Tone.NoiseSynth({ noise: { type: "pink" }, envelope: { attack: 0.001, decay: 0.15, sustain: 0 } });
      clapSynth.volume.value = -6;
      // Pre-connect drum synths to the master EQ as a safety net (drums
      // without a corresponding track lane still fire into the master).
      kickSynth.connect(eq);
      snareSynth.connect(eq);
      hihatSynth.connect(eq);
      clapSynth.connect(eq);

      const fireDrum = (slot, time, vel = 1, synthFallback, trackChainInput) => {
        const p = drumPlayers[slot];
        if (p) {
          try {
            // Route this drum player through the drum track's FX chain by
            // creating a per-firing player. Tone.Player.start() can only be
            // called once for buffer-based playback in offline, so we clone
            // by re-using the buffer with a fresh Tone.Player each hit.
            const dst = trackChainInput || eq;
            const oneShot = new Tone.Player(drumBuffers[slot]).connect(dst);
            oneShot.volume.value = vel >= 1 ? 0 : (20 * Math.log10(Math.max(0.001, vel)));
            oneShot.start(time);
            return;
          } catch { /* fall through */ }
        }
        try { synthFallback(time, vel); } catch { /* noop */ }
      };

      // ---- Bass / Melody samplers (pre-decoded buffers only) --------------
      // These are the SHARED admin-kit samplers (default bass/melody kits),
      // used when a track's dedicated per-track sampler wasn't built. They
      // connect to the master EQ (no track chain) since they're shared.
      let bassSampler = null;
      if (Object.keys(bassBuffers).length > 0) {
        try { bassSampler = new Tone.Sampler({ urls: bassBuffers }).connect(eq); } catch { bassSampler = null; }
      }
      let melodySampler = null;
      if (melodyMeta && melodyBuffer) {
        try { melodySampler = new Tone.Sampler({ urls: { [melodyMeta.rootPitch]: melodyBuffer } }).connect(eq); } catch { melodySampler = null; }
      }

      const synths = {};
      const samplers = {};
      const samplerPadPlayers = {};
      const audioPlayers = {};

      tracks.forEach((t) => {
        // v9.7 — route every voice through this track's full serial chain:
        //   voice → fxChain → panner → volume → master EQ
        // buildTrackChain returns the CHAIN INPUT node; volumes are handled
        // inside the chain so we no longer need per-voice .volume.value.
        const chainIn = buildTrackChain(t);
        // Mirror the EXACT voice specs from ensureTrackInstrument so the
        // offline render matches real-time 1:1 — synth ADSR / oscillator /
        // portamento / bass filter env / melody spread are all preserved.
        if (t.type === "synth") {
          // v9.9.10 — read producer's persisted synthConfig so the WAV
          // EXPORT plays with the EXACT oscillator + ADSR they hear live.
          // Previously hardcoded → plucky/lead voicings collapsed into a
          // long-sustain default. Fx chain rebuilt via buildTrackChain
          // above already routes reverb/delay/eq5/distortion offline.
          const cfg = t.synthConfig || {};
          const env = cfg.envelope || { attack: 0.02, decay: 0.25, sustain: 0.55, release: 1.1 };
          const oscType = cfg.oscType || "fatsawtooth";
          const s = new Tone.PolySynth(Tone.Synth, {
            oscillator: oscType === "fatsawtooth" ? { type: "fatsawtooth", count: 3, spread: 30 } : { type: oscType },
            envelope: env,
            portamento: t.glide ?? 0,
          }).connect(chainIn);
          s.volume.value = -10;
          synths[t.id] = s;
        }
        if (t.type === "bass") {
          const s = new Tone.MonoSynth({
            oscillator: { type: "sawtooth" },
            filter: { Q: 4, type: "lowpass", rolloff: -24 },
            envelope: { attack: 0.005, decay: 0.2, sustain: 0.6, release: 0.05 },
            filterEnvelope: { attack: 0.01, decay: 0.2, sustain: 0.3, release: 0.1, baseFrequency: 60, octaves: 4 },
            portamento: t.glide ?? 0,
          }).connect(chainIn);
          s.volume.value = -8;
          synths[t.id] = s;
        }
        if (t.type === "melody") {
          const s = new Tone.PolySynth(Tone.Synth, {
            oscillator: { type: "fatsawtooth", count: 3, spread: 25 },
            envelope: { attack: 0.02, decay: 0.25, sustain: 0.55, release: 0.15 },
            portamento: t.glide ?? 0,
          }).connect(chainIn);
          s.volume.value = -10;
          synths[t.id] = s;
        }
        if (t.type === "sampler") {
          const buf = trackSamplerBuffers[t.id];
          const rootPitch = t.samplerRootPitch || "C4";
          if (buf) {
            try {
              samplers[t.id] = new Tone.Sampler({
                urls: { [rootPitch]: buf },
                attack: t.samplerEnv?.attack ?? 0.005,
                release: t.samplerEnv?.release ?? 0.2,
                portamento: t.glide ?? 0,
              }).connect(chainIn);
              samplers[t.id].volume.value = -3;
            } catch { /* skip lane */ }
          }
          samplerPadPlayers[t.id] = {};
          (t.samplerPads || []).forEach((pad) => {
            const padBuf = padBuffers[t.id]?.[pad.id];
            if (padBuf) {
              try { samplerPadPlayers[t.id][pad.id] = new Tone.Player(padBuf).connect(chainIn); } catch { /* skip pad */ }
            }
          });
        }
        if (t.type === "audio") {
          audioPlayers[t.id] = {};
          (t.audioClips || []).forEach((clip) => {
            const clipBuf = audioClipBuffers[t.id]?.[clip.id];
            if (clipBuf) {
              try { audioPlayers[t.id][clip.id] = new Tone.Player(clipBuf).connect(chainIn); } catch { /* skip clip */ }
            }
          });
        }
      });

      // No Tone.loaded() — every sample-based instrument was built from a
      // pre-decoded AudioBuffer and is instantly ready.
      const hasSoloed = tracks.some((t) => t.solo);

      // Render MIDI from regions
      regions.forEach((r) => {
        const tr = tracks.find((tt) => tt.id === r.trackId);
        if (!tr || tr.muted) return;
        if (hasSoloed && !tr.solo) return;
        const stepsInRegion = r.length * stepsPerBar;
        for (let s = 0; s < stepsInRegion; s++) {
          // v19.3 — Use the REGION-RELATIVE ABSOLUTE step for pattern/notes
          // lookups. Previously this was `s % 16`, which collapsed every
          // bar into bar 1's slice and made the export loop bar-1 content
          // for the entire multi-bar render. (Real-time playback was
          // already correct via v17.3.)
          const stepInBar = s;
          const t = r.bar * secPerBar + s * secondsPerStep;
          const pattern = r.pattern || [];
          const notes = r.notes || [];
          if (tr.type === "kick" && pattern[stepInBar]) {
            fireDrum("kick", t, 1, (time) => kickSynth.triggerAttackRelease("C1", "8n", time), trackChainInputs[tr.id]);
          } else if (tr.type === "snare" && pattern[stepInBar]) {
            fireDrum("snare", t, 0.9, (time, vel) => snareSynth.triggerAttackRelease("16n", time, vel), trackChainInputs[tr.id]);
          } else if (tr.type === "hihat" && pattern[stepInBar]) {
            fireDrum("hihat", t, 0.5, (time, vel) => hihatSynth.triggerAttackRelease("C4", "32n", time, vel), trackChainInputs[tr.id]);
          } else if (tr.type === "clap" && pattern[stepInBar]) {
            fireDrum("clap", t, 0.8, (time, vel) => clapSynth.triggerAttackRelease("16n", time, vel), trackChainInputs[tr.id]);
          } else if (tr.type === "bass") {
            // High-res match (iter 24): paintbrush 1/32 & 1/64 notes land at
            // fractional steps (0.5, 0.25, ...). Strict equality dropped them
            // silently — floor + sub-step offset preserves the real-time
            // scheduling behaviour.
            const atStep = notes.filter((n) => Math.floor(n.step) === stepInBar);
            atStep.forEach((n) => {
              const durSec = Math.max(0.05, (n.duration || 1) * secondsPerStep);
              const subOffset = (n.step - stepInBar) * secondsPerStep;
              const fireAt = t + subOffset;
              if (bassSampler) {
                try { bassSampler.triggerAttackRelease(n.pitch, durSec, fireAt, 0.9); return; } catch { /* fall through */ }
              }
              if (synths[tr.id]) synths[tr.id].triggerAttackRelease(n.pitch, durSec, fireAt);
            });
          } else if (tr.type === "melody") {
            const atStep = notes.filter((n) => Math.floor(n.step) === stepInBar);
            atStep.forEach((n) => {
              const durSec = Math.max(0.05, (n.duration || 1) * secondsPerStep);
              const subOffset = (n.step - stepInBar) * secondsPerStep;
              const fireAt = t + subOffset;
              if (melodySampler) {
                try { melodySampler.triggerAttackRelease(n.pitch, durSec, fireAt, 0.85); return; } catch { /* fall through */ }
              }
              if (synths[tr.id]) synths[tr.id].triggerAttackRelease(n.pitch, durSec, fireAt);
            });
          } else if (tr.type === "sampler") {
            const atStep = notes.filter((n) => Math.floor(n.step) === stepInBar);
            const oneShot = !!tr.samplerOneShot;
            const rootPitch = tr.samplerRootPitch || "C4";
            atStep.forEach((n) => {
              const subOffset = (n.step - stepInBar) * secondsPerStep;
              const fireAt = t + subOffset;
              const pad = (tr.samplerPads || []).find((p) => p.pitch === n.pitch && p.sampleUrl);
              if (pad && samplerPadPlayers[tr.id]?.[pad.id]) {
                try { samplerPadPlayers[tr.id][pad.id].start(fireAt); } catch { /* ignore */ }
              } else if (samplers[tr.id]) {
                const durSec = Math.max(0.05, (n.duration || 1) * secondsPerStep);
                const firePitch = oneShot ? rootPitch : n.pitch;
                try { samplers[tr.id].triggerAttackRelease(firePitch, durSec, fireAt); } catch { /* ignore */ }
              }
            });
            if (atStep.length === 0 && pattern[stepInBar] && samplers[tr.id]) {
              try { samplers[tr.id].triggerAttackRelease(rootPitch, secondsPerStep, t); } catch { /* ignore */ }
            }
          } else if (tr.type === "synth" && synths[tr.id]) {
            const atStep = notes.filter((n) => Math.floor(n.step) === stepInBar);
            atStep.forEach((n) => {
              const durSec = Math.max(0.05, (n.duration || 1) * secondsPerStep);
              const subOffset = (n.step - stepInBar) * secondsPerStep;
              synths[tr.id].triggerAttackRelease(n.pitch, durSec, t + subOffset);
            });
          }
        }
      });

      // Render audio clips (with offset + duration for trim/crop)
      tracks.forEach((tr) => {
        if (tr.type !== "audio" || tr.muted) return;
        if (hasSoloed && !tr.solo) return;
        (tr.audioClips || []).forEach((clip) => {
          const player = audioPlayers[tr.id]?.[clip.id];
          if (!player) return;
          const startSec = (clip.bar || 0) * secPerBar;
          const offsetSec = Math.max(0, clip.offset || 0);
          const durationSec = Math.max(0.01, (clip.length || 1) * secPerBar);
          try { player.start(startSec, offsetSec, durationSec); } catch {}
        });
      });

      transport.start();
    }, duration);

    return buffer.get();
  }

  // v13.1 — Stem Export. Renders each user-visible track independently
  // via `exportToWav` using ONLY that track's regions/audio-clips,
  // producing an ordered list of `{ name, buffer }` entries the caller
  // can turn into individual WAV files (and JSZip.zip into a single
  // download). Muted/solo state is respected only within the single-
  // track render — if the caller passes a track that's muted, it will
  // still render (stems are usable regardless of live mix state).
  //
  // Duration is calculated per-track: bar 0 → end of that track's last
  // region (or audio clip). The full FX chain, Crow Pumper, Reverb,
  // Delay, EQ, reversed audio, synth config etc. are all preserved
  // because the underlying `exportToWav` already builds the exact same
  // serial chain per track.
  async exportStems(tracks, bpm) {
    const secPerBar = (60 / bpm) * 4;
    const stems = [];
    for (const track of tracks) {
      // Regions attached to this single track.
      const tRegions = (this.regionsRef || []).filter((r) => r.trackId === track.id);
      // Fall back to whatever regions the caller passed via engine state — but
      // for safety in case regionsRef is stale, also look at the store.
      let regions = tRegions;
      try {
        // Read the live regions off the DAW store to guarantee freshness.
        const store = (await import("../store/dawStore")).useDAWStore.getState();
        regions = (store.regions || []).filter((r) => r.trackId === track.id);
      } catch { /* fallback to regionsRef */ }
      // Compute this track's last bar (region end OR audio-clip end).
      let lastBar = 1;
      regions.forEach((r) => { lastBar = Math.max(lastBar, (r.bar || 0) + (r.length || 1)); });
      (track.audioClips || []).forEach((c) => { lastBar = Math.max(lastBar, (c.bar || 0) + (c.length || 1)); });
      if (regions.length === 0 && (track.audioClips || []).length === 0) {
        // Empty track — skip; zip will not contain it.
        continue;
      }
      // Render a solo pass of this single track — pass an unmuted clone so
      // muted tracks in the live mix still show up as stems.
      const soloed = { ...track, muted: false, solo: false };
      // Reversed audio-clip state is preserved in the clip itself — the
      // exportToWav path pre-decodes each clip URL and offlines it through
      // the track chain; reverse is handled by the pre-encoded buffer so
      // it survives the offline render 1:1.
      const buf = await this.exportToWav([soloed], bpm, regions, Math.ceil(lastBar));
      // Safe filename: track.name → alphanum + dash. Prefix with two-digit
      // index so unzip order matches DAW order.
      const idx = String(stems.length + 1).padStart(2, "0");
      const safeName = (track.name || track.type || "track")
        .replace(/[^\w\-]+/g, "_")
        .slice(0, 40);
      stems.push({ name: `${idx}_${safeName}.wav`, buffer: buf, durationSec: buf.duration });
    }
    return stems;
  }
}

const engine = new AudioEngine();
// Diagnostic dump — call `window.engine.debug()` from the browser console to
// verify signal flow when audio silence is reported. Reports AudioContext
// state, transport state, per-track output buses, and every synth/sampler
// voice's connection status.
AudioEngine.prototype.debug = function debug() {
  const ctxState = Tone.getContext()?.rawContext?.state;
  const transport = Tone.getTransport();
  const info = {
    ctxState,
    transportState: transport.state,
    transportPosition: transport.position,
    bpm: transport.bpm.value,
    masterGainDb: this.masterGain?.gain?.value != null ? 20 * Math.log10(Math.max(0.0001, this.masterGain.gain.value)) : "N/A",
    tracks: (this.tracksRef || []).map((t) => ({
      id: t.id, name: t.name, type: t.type,
      muted: !!t.muted, solo: !!t.solo,
      volumeLinear: t.volume,
      volumeDb: this.trackOutputs[t.id]?.volume?.volume?.value ?? "no-bus",
      busMuted: this.trackOutputs[t.id]?.volume?.mute ?? "no-bus",
      hasSynthVoice: !!this.synthVoices[t.id],
      hasSampler: !!this.samplerPlayers[t.id],
      hasSamplerFallback: !!this.samplerFallbackSynths[t.id],
      samplerLoadError: t.samplerLoadError || null,
      samplerRootPitch: t.samplerRootPitch,
      samplerOneShot: !!t.samplerOneShot,
      regionCount: (this.regionsRef || []).filter((r) => r.trackId === t.id).length,
      noteCount: (this.regionsRef || []).filter((r) => r.trackId === t.id).reduce((sum, r) => sum + (r.notes?.length || 0), 0),
    })),
  };
  // eslint-disable-next-line no-console
  console.log("[AudioEngine.debug]", info);
  return info;
};
// Global unhandled promise rejection logger — catches any decode / fetch /
// Tone.Buffer failures that escape try/catch blocks so future audio-silence
// bugs surface with a clear cause instead of vanishing silently.
if (typeof window !== "undefined") {
  window.addEventListener("unhandledrejection", (ev) => {
    // eslint-disable-next-line no-console
    console.warn("[AudioEngine unhandledrejection]", ev.reason?.message || ev.reason);
  });
  try { window.engine = engine; window.Tone = Tone; } catch { /* noop */ }
}
export default engine;
